// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { DataGrid, GridActionsCellItem, GridRowModes, GridRowEditStopReasons, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { AlertColor } from '@mui/material';
import { apiUrl } from '../utils/api';
import { formatCurrency, formatNumber } from '../utils/numberFormatter';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Badge,
  Checkbox,
  Chip,
  LinearProgress,
  Menu,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContractDocuments from '../components/ContractDocuments';

const emptyForm = {
  contract_number: '',
  title: '',
  original_value: '',
  currency: 'SGD',
  signed_date: '',
  rate_sheet_id: '',
  contract_type: 'contract',
  rejection_comments: ''
};

// Budget Summary Component
const BudgetSummary = ({ contractId, companyData, currency }: { contractId: number; companyData: any; currency?: string }) => {
  const [budgetInfo, setBudgetInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBudget = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/api/contracts/${contractId}/budget-summary`));
        if (!response.ok) return;
        const data = await response.json();
        setBudgetInfo(data);
      } catch (error) {
        console.error('Error fetching budget:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contractId) {
      fetchBudget();
    }
  }, [contractId]);

  if (loading) return <LinearProgress />;
  if (!budgetInfo) return null;

  const percentageUsed = budgetInfo.percentage_used || 0;
  const isOverBudget = budgetInfo.status === 'over_budget';

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: isOverBudget ? '#ffebee' : '#e8f5e9', borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>Budget Summary</Typography>
      {/* @ts-ignore */}
      <Grid container spacing={2}>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Contract Value</Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatCurrency(budgetInfo.budget_summary.contract_value, currency || 'SGD', companyData.number_format)}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">PO Committed</Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatCurrency(budgetInfo.budget_summary.po_committed, currency || 'SGD', companyData.number_format)}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">MR Committed</Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatCurrency(budgetInfo.budget_summary.mr_committed, currency || 'SGD', companyData.number_format)}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Remaining</Typography>
          <Typography variant="body1" fontWeight="bold" color={isOverBudget ? 'error' : 'success'}>
            {formatCurrency(budgetInfo.budget_summary.remaining, currency || 'SGD', companyData.number_format)}
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption">Budget Usage: {percentageUsed.toFixed(1)}%</Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentageUsed, 100)}
          color={percentageUsed > 90 ? 'error' : percentageUsed > 75 ? 'warning' : 'success'}
          sx={{ mt: 0.5 }}
        />
      </Box>
      {isOverBudget && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Budget exceeded!
        </Alert>
      )}
    </Box>
  );
};

const QSContractOrVO = ({ projectId, access = 'none', userId = null, deleteAllAccess = 'none' }) => {
  const canWrite = access === 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rateSheets, setRateSheets] = useState([]);
  const [rateSheetItems, setRateSheetItems] = useState([]);
  const [budgetLineItems, setBudgetLineItems] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractRowModesModel] = useState({});
  const [budgetLineRowModesModel, setBudgetLineRowModesModel] = useState({});
  const [pendingSaveId, setPendingSaveId] = useState(null);

  // Removed: expandedRows and contractVOs (VOs now shown at same level)

  // Legacy states for dialog (will be simplified)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [formValues, setFormValues] = useState(emptyForm);

  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedContractForDocs, setSelectedContractForDocs] = useState<any>(null);

  // Import Design Changes states
  const [importDCDialogOpen, setImportDCDialogOpen] = useState(false);
  const [designChanges, setDesignChanges] = useState([]);
  const [selectedDCs, setSelectedDCs] = useState<string[]>([]);
  const [importingDC, setImportingDC] = useState(false);
  const [importDCError, setImportDCError] = useState('');

  // Variation Order dialog states (NEW HIERARCHICAL SYSTEM)
  const [voDialogOpen, setVoDialogOpen] = useState(false);
  const [voFormValues, setVoFormValues] = useState(emptyForm);
  const [voDialogError, setVoDialogError] = useState('');
  const [creatingVO, setCreatingVO] = useState(false);
  const [voSelectedDCs, setVoSelectedDCs] = useState<string[]>([]);
  const [voSelectedBoqItems, setVoSelectedBoqItems] = useState<{[dcNumber: string]: number[]}>({});
  const [voExpandedDCs, setVoExpandedDCs] = useState<string[]>([]);
  const [voDcBoqData, setVoDcBoqData] = useState<{[dcNumber: string]: any[]}>({});
  const [voLoadingBoqData, setVoLoadingBoqData] = useState(false);
  const [budgetLineLoading, setBudgetLineLoading] = useState(false);

  // Copy BOQ confirm dialog
  const [copyBoqConfirmOpen, setCopyBoqConfirmOpen] = useState(false);
  const [copyBoqTargetDC, setCopyBoqTargetDC] = useState<string>('');

  // Manual line items for Create VO and Edit dialogs
  const [voManualLines, setVoManualLines] = useState<any[]>([]);
  const [editManualLines, setEditManualLines] = useState<any[]>([]);
  const [editManualRowModesModel, setEditManualRowModesModel] = useState({});
  const [voManualRowModesModel, setVoManualRowModesModel] = useState({});

  // Company data state for number formatting and document header
  const [companyData, setCompanyData] = useState({ company_name: '', logo_url: '', address: '', phone: '', email: '', number_format: '1,234.56' });

  // Excel Import/Export States
  const [excelImporting, setExcelImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [excelExporting, setExcelExporting] = useState(false);

  useEffect(() => {
    if (!excelImporting) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelImporting]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [currentImportFile, setCurrentImportFile] = useState<File | null>(null);
  // Import source / mapping / summary
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const CONTRACT_FIELDS = ['Contract Number', 'Title', 'Original Value', 'Currency', 'Signed Date', 'Contract Type'];
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);

  // Delete Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'contract' | 'vo' | 'budgetLine'>('contract');
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  // Delete All Dialog States
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Rejection Reason Dialog
  const [rejectionReasonDialogOpen, setRejectionReasonDialogOpen] = useState(false);
  const [selectedRejectedContract, setSelectedRejectedContract] = useState<any>(null);

  // View Contract/VO Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedViewContract, setSelectedViewContract] = useState<any>(null);
  const [viewBOQLines, setViewBOQLines] = useState<any[]>([]);
  const [viewBOQLoading, setViewBOQLoading] = useState(false);
  const [viewBudgetSummary, setViewBudgetSummary] = useState<any>(null);
  const [viewLinkedDCs, setViewLinkedDCs] = useState<any[]>([]);

  // Add Contract/VO Menu State
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
  const addMenuOpen = Boolean(addMenuAnchorEl);

  const saveButtonRef = useRef(null);
  const processingRef = useRef(false);



  const fetchContracts = useCallback(() => {
    if (!projectId || access === 'none') return;
    setLoading(true);
    setBudgetLineLoading(true);
    setPageError('');
    fetch(apiUrl(`/api/contracts?project_id=${projectId}&include_lines=true`), { headers: { ...(userId ? { 'X-User-ID': userId } : {}) } })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load contracts');
        return data;
      })
      .then(data => {
        // Show all contracts including Rejected (so QS can see and resubmit)
        const allContracts = Array.isArray(data) ? data : [];
        const filteredContracts = allContracts;
        setContracts(filteredContracts);
        setSelectedContract(prev => {
          if (!prev) return prev;
          return allContracts.find(item => item.contract_id === prev.contract_id) || null;
        });
      })
      .catch(err => setPageError(err.message))
      .finally(() => {
        setLoading(false);
        setBudgetLineLoading(false);
      });
  }, [projectId, access, userId]);

  const fetchCategories = useCallback(() => {
    if (!projectId || access === 'none') return;
    fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load categories');
        return data;
      })
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [projectId, access]);

  // Removed: fetchVOsForContract (VOs now fetched with contracts at same level)

  // Excel Export Handler
  const handleExportExcel = async () => {
    try {
      setExcelExporting(true);
      const url = projectId
        ? apiUrl(`/api/contracts/export?project_id=${projectId}`)
        : apiUrl('/api/contracts/export');

      const response = await fetch(url, {
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to export contracts');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `contracts_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSnackbar({ open: true, message: 'Contracts exported successfully!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setExcelExporting(false);
    }
  };

  // Excel Template Download Handler
  const handleImportClick = () => setImportSourceOpen(true);

  const handleImportSource = (isThird: boolean) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMapFile(null);
      setFileHeaders([]);
      setMapping({});
      setMappingOpen(true);
    } else { fileInputRef.current?.click(); }
  };

  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      const auto = { ...mapping };
      CONTRACT_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch { setFileHeaders([]); }
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const norm = rows.map(r => CONTRACT_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: r[mapping[fld]] ?? '' }), {}));
      // Convert back to file and send to backend
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingOpen(false);
      await runImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runImport = async (file: File) => {
    try {
      setExcelImporting(true);
      setImportProgress(0);
      const formData = new FormData();
      formData.append('file', file);
      const url = projectId ? apiUrl(`/api/contracts/import?project_id=${projectId}`) : apiUrl('/api/contracts/import');
      const response = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import contracts');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_, i) => ({ label: `Contract ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_, i) => ({ label: `Contract ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_, i) => ({ label: `Existing Contract ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportProgress(100);
      setImportSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportSummaryOpen(true);
      fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (err: any) {
      setImportProgress(0);
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally { setExcelImporting(false); }
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, contract: r.label, status, notes }
    })
    fetch(apiUrl('/api/contracts/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'Contract_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(apiUrl('/api/contracts/template'), {
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download template');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'contracts_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSnackbar({ open: true, message: 'Template downloaded successfully!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Excel Import Handler
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input immediately
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      setExcelImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = projectId
        ? apiUrl(`/api/contracts/import?project_id=${projectId}`)
        : apiUrl('/api/contracts/import');

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import contracts');
      }

      // Store results and show dialog
      setImportResults(data);
      setCurrentImportFile(file);
      setImportDialogOpen(true);

      // Refresh contracts list
      fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setExcelImporting(false);
    }
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportResults(null);
    setCurrentImportFile(null);
  };

  const fetchRateSheets = useCallback(() => {
    if (!projectId || access === 'none') return;
    console.log('🔄 Fetching rate sheets for project:', projectId);
    fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load rate sheets');
        return data;
      })
      .then(data => {
        console.log('📥 Received rate sheets:', data);
        setRateSheets(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('❌ Failed to fetch rate sheets:', err);
        setRateSheets([]);
      });

    // Fetch Budget Line Items for the project and enrich with prices from rate sheet
    if (projectId) {
      // Fetch all budget line items first
      fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          return res.json();
        })
        .then(budgetItems => {
          const items = Array.isArray(budgetItems) ? budgetItems : [];
          console.log('💰 Budget Line Items fetched:', items.length, 'items');

          // Now try to fetch rate sheet items to get current prices
          fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
            .then(res => res.json())
            .then(rateSheets => {
              const activeSheet = Array.isArray(rateSheets)
                ? rateSheets.find(rs => rs.is_active) || rateSheets[0]
                : null;

              if (activeSheet) {
                console.log('🔍 Active rate sheet found:', activeSheet.rate_sheet_id);
                // Fetch rate sheet items to get prices
                fetch(apiUrl(`/api/rate-sheets/${activeSheet.rate_sheet_id}/items`))
                  .then(res => res.json())
                  .then(rateItems => {
                    const rateItemsArray = Array.isArray(rateItems) ? rateItems : [];
                    console.log('📋 Rate sheet items fetched for enrichment:', rateItemsArray.length);

                    // Merge: for each budget item, add unit_price from rate sheet if available
                    const enrichedItems = items.map(item => {
                      const rateItem = rateItemsArray.find(ri => ri.bli_id === item.bli_id);
                      return {
                        ...item,
                        unit_price: rateItem ? rateItem.unit_price : 0
                      };
                    });

                    console.log('✅ Enriched budget items with prices:', enrichedItems.length);
                    console.log('✅ First enriched item:', enrichedItems[0]);
                    setBudgetLineItems(enrichedItems);
                  })
                  .catch(() => {
                    // If rate sheet items fetch fails, use budget items with 0 price
                    console.log('⚠️ Failed to fetch rate sheet items, using budget items with 0 price');
                    setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
                  });
              } else {
                // No rate sheet, use budget items with 0 price
                console.log('⚠️ No active rate sheet found, using budget items with 0 price');
                setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
              }
            })
            .catch(() => {
              // If rate sheet fetch fails, use budget items with 0 price
              console.log('⚠️ Failed to fetch rate sheets, using budget items with 0 price');
              setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
            });
        })
        .catch(err => {
          console.error('Failed to fetch budget line items:', err);
          setBudgetLineItems([]);
        });
    }
    // Rate sheet items will also be fetched when a contract with a rate sheet is selected
  }, [projectId, access]);

  const fetchRateSheetItems = useCallback((rateSheetId) => {
    if (rateSheetId) {
      console.log('🔄 Fetching rate sheet items for rate sheet:', rateSheetId);
      fetch(apiUrl(`/api/rate-sheets/${rateSheetId}/items`))
        .then(async res => {
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to load rate sheet items');
          return data;
        })
        .then(data => {
          console.log('📋 Received rate sheet items:', data);
          setRateSheetItems(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error('❌ Failed to fetch rate sheet items:', err);
          setRateSheetItems([]);
        });
    } else {
      setRateSheetItems([]);
    }
  }, []);

  const fetchDesignChanges = useCallback(() => {
    if (!projectId || access === 'none') return;
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load design changes');
        return data;
      })
      .then(data => setDesignChanges(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch design changes:', err);
        setDesignChanges([]);
      });
  }, [projectId, access]);

  // Fetch company data for number formatting and document header
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!projectId) {
        setCompanyData({ company_name: '', logo_url: '', address: '', phone: '', email: '', number_format: '1,234.56' });
        return;
      }
      try {
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), { credentials: 'include' });
        const currentProject = await projectRes.json();
        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), { credentials: 'include' });
        const data = await response.json();
        setCompanyData({
          company_name: data.company_name || '',
          logo_url: data.logo_url || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          number_format: data.number_format || '1,234.56'
        });
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompanyData({ company_name: '', logo_url: '', address: '', phone: '', email: '', number_format: '1,234.56' });
      }
    };
    if (projectId) fetchCompanyData();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || access === 'none') {
      setContracts([]);
      setCategories([]);
      setRateSheets([]);
      setDesignChanges([]);
      setSelectedContract(null);
      setPageError('');
      return;
    }
    fetchCategories();
    fetchRateSheets();
    fetchDesignChanges();
    fetchContracts();
  }, [projectId, access, fetchCategories, fetchRateSheets, fetchDesignChanges, fetchContracts]);

  // Handle pending save operations after state updates
  useEffect(() => {
    if (!pendingSaveId || !selectedContract || processingRef.current) {
      return;
    }

    const performSave = async () => {
      try {
        // Prevent duplicate processing
        processingRef.current = true;
        setBudgetLineLoading(true); // Show loading in budget lines DataGrid
        setLoading(true); // Show loading in main contracts table

        console.log('💾 Executing pending save for ID:', pendingSaveId);
        console.log('🔍 selectedContract in useEffect:', selectedContract);
        console.log('🔍 selectedContract.lines:', selectedContract.lines);

        // Find the line to save with current state values
        const lineToSave = selectedContract.lines?.find(line => line.id === pendingSaveId);
        if (!lineToSave) {
          console.error('Line to save not found:', pendingSaveId);
          setSnackbar({ open: true, message: 'Error: Budget line not found', severity: 'error' });
          setPendingSaveId(null);
          setBudgetLineLoading(false);
          setLoading(false);
          return;
        }

        console.log('🔍 Line to save:', lineToSave);

        // For now, update the contract with the new/updated lines
        // Since the backend doesn't have individual budget line endpoints,
        // we'll update the entire contract with the modified lines
        const updatedLines = selectedContract.lines || [];

        // If it's a new line, replace temp ID with a proper structure
        const linesToSave = updatedLines.map(line => ({
          id: String(line.id).startsWith('temp_') ? undefined : line.id,
          line_no: line.line_no,
          description: line.description,
          qty: line.qty,
          uom: line.uom,
          unit_price: line.unit_price,
          category_id: line.category_id || undefined
        }));

        const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
          body: JSON.stringify({
            contract_number: selectedContract.contract_number,
            title: selectedContract.title,
            original_value: selectedContract.original_value,
            currency: selectedContract.currency,
            signed_date: selectedContract.signed_date,
            rate_sheet_id: selectedContract.rate_sheet_id,
            lines: linesToSave
          })
        });

        if (response.ok) {
          const updatedContract = await response.json();
          setSelectedContract(updatedContract);

          // Refetch all contracts to update total values immediately
          await fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));

          setSnackbar({
            open: true,
            message: String(pendingSaveId).startsWith('temp_') ? 'Budget line added successfully' : 'Budget line updated successfully',
            severity: 'success'
          });
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to save budget line', severity: 'error' });
        }
      } catch (saveError) {
        console.error('Error saving budget line:', saveError);
        setSnackbar({ open: true, message: 'Failed to save budget line', severity: 'error' });
      } finally {
        setPendingSaveId(null);
        processingRef.current = false;
        setBudgetLineLoading(false); // Hide loading
        setLoading(false); // Hide loading in main contracts table
      }
    };

    performSave();
  }, [pendingSaveId, selectedContract, userId, fetchContracts]);

  // Process row update for budget lines (inline DataGrid editing)
  const processBudgetLineRowUpdate = useCallback(async (newRow) => {
    console.log('🔄 processBudgetLineRowUpdate called with:', newRow);
    
    if (!canWrite) {
      throw new Error('You do not have permission to edit budget lines.');
    }
    
    if (!selectedContract) {
      throw new Error('No contract selected.');
    }

    // For temp IDs (new lines), only update local state but don't make API calls
    // The manual save button will handle the API call
    if (String(newRow.id).startsWith('temp_')) {
      console.log('🔄 Updating local state only for new line (temp ID):', newRow.id);
      
      // Update the local state with the edited values
      const updatedLines = selectedContract.lines?.map(line => 
        line.id === newRow.id ? { ...line, ...newRow } : line
      ) || [];

      console.log('📝 Updated temp line in state:', newRow);
      
      // Update selectedContract state
      const updatedContract = { ...selectedContract, lines: updatedLines };
      setSelectedContract(updatedContract);
      
      return newRow; // Return the updated row without making API call
    }

    // For existing lines (non-temp IDs), update state and make API call
    try {
      setBudgetLineLoading(true); // Show loading state
      setLoading(true); // Show loading in main contracts table

      // Update the local state immediately
      const updatedLines = selectedContract.lines?.map(line =>
        line.id === newRow.id ? { ...line, ...newRow } : line
      ) || [];

      console.log('🔄 Updated budget line:', newRow);
      console.log('📋 All lines after update:', updatedLines);

      // Update selectedContract state
      const updatedContract = { ...selectedContract, lines: updatedLines };
      setSelectedContract(updatedContract);

      // Prepare the payload for the API call
      const linesToSave = updatedLines.map(line => ({
        id: String(line.id).startsWith('temp_') ? undefined : line.id,
        line_no: line.line_no,
        description: line.description,
        qty: line.qty,
        uom: line.uom,
        unit_price: line.unit_price,
        category_id: line.category_id || undefined
      }));

      console.log('🚀 Saving budget lines via API:', linesToSave);

      const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify({
          contract_number: selectedContract.contract_number,
          title: selectedContract.title,
          original_value: selectedContract.original_value,
          currency: selectedContract.currency,
          signed_date: selectedContract.signed_date,
          rate_sheet_id: selectedContract.rate_sheet_id,
          lines: linesToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update budget line');
      }

      const updatedContractFromAPI = await response.json();
      console.log('✅ Budget line updated successfully:', updatedContractFromAPI);

      // Update local state with response from API
      setSelectedContract(updatedContractFromAPI);

      // Refetch all contracts to update total values immediately
      await fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));

      setBudgetLineLoading(false); // Hide loading state
      setLoading(false); // Hide loading in main contracts table

      // Show success message
      setSnackbar({
        open: true,
        message: 'Budget line updated successfully',
        severity: 'success'
      });

      return newRow; // Return the updated row

    } catch (error) {
      setBudgetLineLoading(false); // Hide loading state on error
      setLoading(false); // Hide loading in main contracts table on error
      console.error('❌ Error updating budget line:', error);
      setSnackbar({
        open: true,
        message: `Error updating budget line: ${error.message}`,
        severity: 'error'
      });
      throw error; // This will revert the row to its original state
    }
  }, [canWrite, selectedContract, userId, fetchContracts]);

  // selectedContract is now managed as state instead of computed

  // Menu handlers
  const handleOpenAddMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleOpenCreate = () => {
    handleCloseAddMenu();
    setDialogMode('create');
    setDialogError('');
    setFormValues({ ...emptyForm });
    setDialogOpen(true);
  };

  const handleOpenCreateVOFromMenu = () => {
    handleCloseAddMenu();
    handleOpenCreateVO();
  };

  const handleOpenEdit = useCallback(async (contract) => {
    if (!contract) return;
    setDialogMode('edit');
    setDialogError('');
    setSelectedContract(contract);
    console.log('🔍 Opening edit for contract:', contract);
    console.log('🔍 Contract rate_sheet_id value:', contract.rate_sheet_id);
    const formData = {
      contract_number: contract.contract_number || '',
      title: contract.title || '',
      original_value: contract.original_value !== null && contract.original_value !== undefined ? String(contract.original_value) : '',
      currency: contract.currency || 'SGD',
      signed_date: contract.signed_date || '',
      rate_sheet_id: contract.rate_sheet_id || '',
      contract_type: contract.contract_type || 'contract',
      rejection_comments: contract.rejection_comments || ''
    };
    console.log('🔍 Setting form values with rate_sheet_id:', formData.rate_sheet_id);
    setFormValues(formData);

    // Fetch rate sheet items if a rate sheet is selected
    if (formData.rate_sheet_id) {
      fetchRateSheetItems(formData.rate_sheet_id);
    }

    // If editing a VO, fetch design changes and pre-select existing BOQ items
    if (contract.contract_type === 'variation_order') {
      // Fetch design changes
      try {
        const dcResponse = await fetch(apiUrl(`/api/design-changes?project_id=${projectId}`), {
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
        });
        const dcData = await dcResponse.json();
        setDesignChanges(Array.isArray(dcData) ? dcData : []);
      } catch (err) {
        console.error('Failed to fetch design changes:', err);
      }

      // Fetch existing BOQ items for this VO
      try {
        const boqResponse = await fetch(apiUrl(`/api/contracts/${contract.contract_id}/lines`), {
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
        });
        const boqData = await boqResponse.json();
        console.log('📦 Fetched VO BOQ items:', boqData);

        // Group BOQ items by their source DC and pre-select them
        const selectedByDC: Record<string, number[]> = {};
        const dcBoqDataMap: Record<string, any[]> = {};
        const expandedDCs: string[] = [];

        if (Array.isArray(boqData)) {
          for (const item of boqData) {
            console.log('📝 Processing BOQ item:', item);
            const sourceChangeNumber = item.source_change_number;
            if (sourceChangeNumber) {
              if (!selectedByDC[sourceChangeNumber]) {
                selectedByDC[sourceChangeNumber] = [];
                expandedDCs.push(sourceChangeNumber); // Auto-expand DCs with selected items
              }
              if (item.source_dc_boq_line_id) {
                selectedByDC[sourceChangeNumber].push(item.source_dc_boq_line_id);
              }
            }
          }

          console.log('✅ Selected items by DC:', selectedByDC);
          console.log('✅ DCs to expand:', expandedDCs);

          // Fetch BOQ data for each DC that has items
          for (const dcNum of Object.keys(selectedByDC)) {
            try {
              const dcBoqResponse = await fetch(apiUrl(`/api/design-changes/${dcNum}/boq`));
              const dcBoqData = await dcBoqResponse.json();
              dcBoqDataMap[dcNum] = Array.isArray(dcBoqData) ? dcBoqData : [];
              console.log(`📋 Fetched BOQ for DC ${dcNum}:`, dcBoqDataMap[dcNum]);
            } catch (err) {
              console.error(`❌ Failed to fetch BOQ for DC ${dcNum}:`, err);
            }
          }
        }

        // Also fetch DCs linked via Contract Design Changes page (may not have budget lines)
        try {
          const cdcResponse = await fetch(apiUrl(`/api/contract-design-changes?contract_id=${contract.contract_id}`), {
            headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
          });
          const cdcData = await cdcResponse.json();
          console.log('📋 Fetched Contract Design Changes:', cdcData);

          if (Array.isArray(cdcData)) {
            for (const cdc of cdcData) {
              const changeNumber = cdc.change_number;
              if (changeNumber && !selectedByDC[changeNumber]) {
                // DC is linked but has no budget lines yet
                // Fetch BOQ data for this DC first
                try {
                  const dcBoqResponse = await fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`));
                  const dcBoqData = await dcBoqResponse.json();
                  const boqItems = Array.isArray(dcBoqData) ? dcBoqData : [];
                  dcBoqDataMap[changeNumber] = boqItems;

                  // Select ALL BOQ items from this DC by default (since it's linked)
                  const allBoqLineIds = boqItems.map((item: any) => item.dc_boq_line_id).filter((id: any) => id);
                  selectedByDC[changeNumber] = allBoqLineIds;
                  expandedDCs.push(changeNumber);

                  console.log(`📋 Auto-selected ${allBoqLineIds.length} BOQ items for linked DC ${changeNumber}`);
                } catch (err) {
                  console.error(`❌ Failed to fetch BOQ for DC ${changeNumber}:`, err);
                  // Even if fetch fails, add DC to list with empty selection
                  selectedByDC[changeNumber] = [];
                  expandedDCs.push(changeNumber);
                }
              }
            }
          }
        } catch (err) {
          console.error('❌ Failed to fetch Contract Design Changes:', err);
        }

        setVoSelectedBoqItems(selectedByDC);
        setVoDcBoqData(dcBoqDataMap);
        setVoExpandedDCs(expandedDCs); // Auto-expand DCs with pre-selected items

        // Pre-populate editManualLines with ALL existing budget lines (BOQ + manual)
        const existingAllLines: any[] = Array.isArray(boqData)
          ? boqData.map((item: any) => ({
              id: item.id,
              description: item.description || '',
              qty: item.qty || 1,
              uom: item.uom || '',
              unit_price: item.unit_price || 0,
              category_id: item.category_id != null ? String(item.category_id) : '',
              pm_comment: item.pm_comment || '',
              source_change_number: item.source_change_number || null,
              source_dc_boq_line_id: item.source_dc_boq_line_id || null
            }))
          : [];

        // Also add BOQ items from DCs that are linked (via CDC) but have no budget lines yet
        const existingDcBoqLineIds = new Set(existingAllLines.map(l => l.source_dc_boq_line_id).filter(Boolean));
        for (const [dcNum, dcItems] of Object.entries(dcBoqDataMap)) {
          const existingLinesDC = existingAllLines.filter(l => l.source_change_number === dcNum);
          if (existingLinesDC.length === 0 && Array.isArray(dcItems) && dcItems.length > 0) {
            // DC is linked but has no budget lines yet — add its BOQ items
            (dcItems as any[]).forEach((item: any, i: number) => {
              if (!existingDcBoqLineIds.has(item.dc_boq_line_id)) {
                existingAllLines.push({
                  id: `ml_${Date.now()}_${i}_${item.dc_boq_line_id}`,
                  description: item.item_description || '',
                  qty: item.qty || 1,
                  uom: item.unit_code || '',
                  unit_price: item.unit_price || 0,
                  category_id: '',
                  source_change_number: dcNum,
                  source_dc_boq_line_id: item.dc_boq_line_id
                });
              }
            });
          }
        }

        setEditManualLines(existingAllLines);
        console.log('✅ Pre-selection complete');
      } catch (err) {
        console.error('❌ Failed to fetch VO BOQ items:', err);
      }
    } else {
      // Contract edit: fetch existing BOQ lines and pre-populate the
      // editable grid. Contracts have no source-DC linking, so this is
      // simpler than the VO branch above.
      try {
        const boqResponse = await fetch(apiUrl(`/api/contracts/${contract.contract_id}/lines`), {
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
        });
        const boqData = await boqResponse.json();
        const existingLines: any[] = Array.isArray(boqData)
          ? boqData.map((item: any) => ({
              id: item.id,
              description: item.description || '',
              qty: item.qty || 1,
              uom: item.uom || '',
              unit_price: item.unit_price || 0,
              category_id: item.category_id != null ? String(item.category_id) : '',
              pm_comment: item.pm_comment || '',
              source_change_number: null,
              source_dc_boq_line_id: null
            }))
          : [];
        setEditManualLines(existingLines);
      } catch (err) {
        console.error('❌ Failed to fetch Contract BOQ items:', err);
        setEditManualLines([]);
      }
    }

    setDialogOpen(true);
  }, [fetchRateSheetItems, projectId, userId]);

  const handleCloseDialog = () => {
    // Clear focus to prevent accessibility warnings about focused elements being hidden
    if (document.activeElement && 'blur' in document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    setDialogOpen(false);
    setSaving(false);
    setDialogError('');
    setRateSheetItems([]); // Clear rate sheet items when dialog closes

    // Clear VO-related state when closing dialog
    setVoSelectedBoqItems({});
    setVoDcBoqData({});
    setVoExpandedDCs([]);
    setEditManualLines([]);
  };

  // Import Design Changes handlers
  const handleOpenImportDC = () => {
    if (!selectedContract) {
      setSnackbar({ open: true, message: 'Please select a contract first', severity: 'warning' });
      return;
    }
    setImportDCError('');
    setSelectedDCs([]);
    setImportDCDialogOpen(true);
  };

  const handleCloseImportDC = () => {
    setImportDCDialogOpen(false);
    setImportDCError('');
    setSelectedDCs([]);
    setImportingDC(false);
  };

  const handleDCSelect = (changeNumber: string) => {
    setSelectedDCs(prev =>
      prev.includes(changeNumber)
        ? prev.filter(cn => cn !== changeNumber)
        : [...prev, changeNumber]
    );
  };

  const handleImportDCSubmit = async () => {
    if (selectedDCs.length === 0) {
      setImportDCError('Please select at least one design change');
      return;
    }
    if (!selectedContract) return;

    setImportingDC(true);
    setImportDCError('');

    try {
      const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}/compile-dc-boq`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify({
          change_numbers: selectedDCs,
          compiled_by: userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import design changes');
      }

      setSnackbar({
        open: true,
        message: `Successfully imported: ${data.merged_count} merged, ${data.added_count} added`,
        severity: 'success'
      });

      handleCloseImportDC();
      fetchContracts(); // Refresh to get updated contract lines
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (err: any) {
      setImportDCError(err.message);
    } finally {
      setImportingDC(false);
    }
  };

  // Variation Order handlers (UPDATED - NO PARENT CONTRACT)
  const handleOpenCreateVO = useCallback(() => {
    setVoDialogError('');
    setVoFormValues({
      ...emptyForm,
      currency: 'SGD'
    });
    setVoSelectedDCs([]);
    setVoSelectedBoqItems({});
    setVoExpandedDCs([]);
    setVoDcBoqData({});

    // Fetch design changes for DC selection
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`), {
      headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
    })
      .then(res => res.json())
      .then(data => {
        setDesignChanges(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch design changes:', err);
      });

    setVoDialogOpen(true);
  }, [projectId, userId]);

  const handleCloseVODialog = () => {
    setVoDialogOpen(false);
    setCreatingVO(false);
    setVoDialogError('');
    setVoSelectedDCs([]);
    setVoSelectedBoqItems({});
    setVoExpandedDCs([]);
    setVoDcBoqData({});
    setVoManualLines([]);
  };

  const addVoManualLine = () => {
    const newId = `ml_${Date.now()}`;
    setVoManualLines(prev => [...prev, { id: newId, description: '', qty: 1, uom: '', unit_price: 0, category_id: '' }]);
    setVoManualRowModesModel(prev => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'description' } }));
  };

  const removeVoManualLine = (id: string) => {
    const lineToRemove = voManualLines.find(l => l.id === id);
    if (lineToRemove?.source_dc_boq_line_id && lineToRemove?.source_change_number) {
      setVoSelectedBoqItems(prev => ({
        ...prev,
        [lineToRemove.source_change_number]: (prev[lineToRemove.source_change_number] || []).filter(bId => bId !== lineToRemove.source_dc_boq_line_id)
      }));
    }
    setVoManualLines(prev => prev.filter(l => l.id !== id));
  };

  const updateVoManualLine = (id: string, field: string, value: any) => {
    setVoManualLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const updateVoManualLineFields = (id: string, fields: Record<string, any>) => {
    setVoManualLines(prev => prev.map(l => l.id === id ? { ...l, ...fields } : l));
  };

  const addEditManualLine = () => {
    const newId = `ml_${Date.now()}`;
    setEditManualLines(prev => [...prev, { id: newId, description: '', qty: 1, uom: '', unit_price: 0, category_id: '' }]);
    setEditManualRowModesModel(prev => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'description' } }));
  };

  const removeEditManualLine = (id: string) => {
    const lineToRemove = editManualLines.find(l => l.id === id);
    // If it's a BOQ line, also uncheck it in the DC checkbox section
    if (lineToRemove?.source_dc_boq_line_id && lineToRemove?.source_change_number) {
      setVoSelectedBoqItems(prev => ({
        ...prev,
        [lineToRemove.source_change_number]: (prev[lineToRemove.source_change_number] || []).filter(bId => bId !== lineToRemove.source_dc_boq_line_id)
      }));
    }
    setEditManualLines(prev => prev.filter(l => l.id !== id));
  };

  const updateEditManualLine = (id: string, field: string, value: any) => {
    setEditManualLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const updateEditManualLineFields = (id: string, fields: Record<string, any>) => {
    setEditManualLines(prev => prev.map(l => l.id === id ? { ...l, ...fields } : l));
  };

  // DataGrid processRowUpdate for Edit dialog manual lines
  const processEditManualLineRowUpdate = (newRow: any) => {
    setEditManualLines(prev => prev.map(l => l.id === newRow.id ? { ...l, ...newRow } : l));
    return newRow;
  };

  // DataGrid processRowUpdate for Create VO dialog manual lines
  const processVoManualLineRowUpdate = (newRow: any) => {
    setVoManualLines(prev => prev.map(l => l.id === newRow.id ? { ...l, ...newRow } : l));
    return newRow;
  };

  // Column definitions for Edit dialog budget line DataGrid
  const editManualLineColumns: GridColDef[] = useMemo(() => [
    {
      field: 'line_no',
      headerName: '#',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const index = editManualLines.findIndex(l => l.id === params.id);
        const isBoqLine = !!params.row.source_change_number;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{index + 1}</span>
            {isBoqLine && <Chip label="DC" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', ml: 0.5 }} />}
          </Box>
        );
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      editable: true,
      renderEditCell: (params) => {
        const itemsToShow = rateSheetItems.length > 0 ? rateSheetItems : budgetLineItems;
        return (
          <Autocomplete
            fullWidth
            size="small"
            freeSolo
            options={itemsToShow}
            getOptionLabel={(option: any) => typeof option === 'string' ? option : option.item_description || ''}
            value={itemsToShow.find((item: any) => item.item_description === params.value) || params.value || ''}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue === 'object') {
                const category = categories.find((cat: any) => cat.name?.toLowerCase() === newValue.category_name?.toLowerCase());
                params.api.setEditCellValue({ id: params.id, field: 'description', value: newValue.item_description || '' });
                setTimeout(() => {
                  try { params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newValue.unit_price || 0 }); } catch (e) {}
                  try { params.api.setEditCellValue({ id: params.id, field: 'uom', value: newValue.unit_code || '' }); } catch (e) {}
                  try { params.api.setEditCellValue({ id: params.id, field: 'category_id', value: category ? category.category_id : '' }); } catch (e) {}
                }, 100);
              }
            }}
            onInputChange={(_, value) => {
              params.api.setEditCellValue({ id: params.id, field: 'description', value });
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                variant="standard"
                placeholder="Search and select item..."
                InputProps={{ ...inputParams.InputProps, disableUnderline: true, sx: { fontSize: '0.875rem' } }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.item_description}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.category_name} • {option.unit_code} • ${option.unit_price}</Typography>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => options.filter(option =>
              option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
            )}
            sx={{ width: '100%' }}
          />
        );
      }
    },
    { field: 'qty', headerName: 'Qty', width: 80, type: 'number', editable: true, align: 'right', headerAlign: 'right' },
    { field: 'uom', headerName: 'UOM', width: 80, editable: true },
    { field: 'unit_price', headerName: 'Unit Price', width: 110, type: 'number', editable: true, align: 'right', headerAlign: 'right' },
    {
      field: 'category_id',
      headerName: 'Category',
      width: 150,
      type: 'singleSelect',
      valueOptions: [{ value: '', label: '-- None --' }, ...categories.map((cat: any) => ({ value: cat.category_id, label: cat.name }))],
      editable: true,
      renderCell: (params) => {
        const category = categories.find((cat: any) => cat.category_id === params.value);
        return category ? category.name : '';
      }
    },
    {
      field: 'line_total',
      headerName: 'Total',
      width: 110,
      editable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => {
        if (!row) return 0;
        return formatNumber((row.qty || 0) * (row.unit_price || 0), companyData.number_format, 2);
      },
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>
    },
    ...(editManualLines.some(l => l.pm_comment) ? [{
      field: 'pm_comment',
      headerName: 'PM Comment',
      flex: 1,
      minWidth: 120,
      editable: false,
      renderCell: (params: any) => (
        <Typography variant="body2" sx={{ color: params.value ? 'warning.dark' : '#bbb', fontStyle: params.value ? 'normal' : 'italic', fontSize: '0.8rem' }}>
          {params.value || '-'}
        </Typography>
      )
    }] : []),
    {
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: any }) => {
        const isInEditMode = editManualRowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<SaveIcon />} label="Save" color="primary" onClick={() => setEditManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }))} />,
            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={() => setEditManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }))} color="inherit" />
          ];
        }
        return [
          <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => setEditManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }))} color="inherit" />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => removeEditManualLine(String(id))} color="inherit" />
        ];
      }
    }
  ], [editManualLines, editManualRowModesModel, rateSheetItems, budgetLineItems, categories, companyData.number_format]);

  // Column definitions for Create VO dialog budget line DataGrid
  const voManualLineColumns: GridColDef[] = useMemo(() => [
    {
      field: 'line_no',
      headerName: '#',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const index = voManualLines.findIndex(l => l.id === params.id);
        const isBoqLine = !!params.row.source_change_number;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{index + 1}</span>
            {isBoqLine && <Chip label="DC" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', ml: 0.5 }} />}
          </Box>
        );
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      editable: true,
      renderEditCell: (params) => {
        const itemsToShow = rateSheetItems.length > 0 ? rateSheetItems : budgetLineItems;
        return (
          <Autocomplete
            fullWidth
            size="small"
            freeSolo
            options={itemsToShow}
            getOptionLabel={(option: any) => typeof option === 'string' ? option : option.item_description || ''}
            value={itemsToShow.find((item: any) => item.item_description === params.value) || params.value || ''}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue === 'object') {
                const category = categories.find((cat: any) => cat.name?.toLowerCase() === newValue.category_name?.toLowerCase());
                params.api.setEditCellValue({ id: params.id, field: 'description', value: newValue.item_description || '' });
                setTimeout(() => {
                  try { params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newValue.unit_price || 0 }); } catch (e) {}
                  try { params.api.setEditCellValue({ id: params.id, field: 'uom', value: newValue.unit_code || '' }); } catch (e) {}
                  try { params.api.setEditCellValue({ id: params.id, field: 'category_id', value: category ? category.category_id : '' }); } catch (e) {}
                }, 100);
              }
            }}
            onInputChange={(_, value) => {
              params.api.setEditCellValue({ id: params.id, field: 'description', value });
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                variant="standard"
                placeholder="Search and select item..."
                InputProps={{ ...inputParams.InputProps, disableUnderline: true, sx: { fontSize: '0.875rem' } }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.item_description}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.category_name} • {option.unit_code} • ${option.unit_price}</Typography>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => options.filter(option =>
              option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
            )}
            sx={{ width: '100%' }}
          />
        );
      }
    },
    { field: 'qty', headerName: 'Qty', width: 80, type: 'number', editable: true, align: 'right', headerAlign: 'right' },
    { field: 'uom', headerName: 'UOM', width: 80, editable: true },
    { field: 'unit_price', headerName: 'Unit Price', width: 110, type: 'number', editable: true, align: 'right', headerAlign: 'right' },
    {
      field: 'category_id',
      headerName: 'Category',
      width: 150,
      type: 'singleSelect',
      valueOptions: [{ value: '', label: '-- None --' }, ...categories.map((cat: any) => ({ value: cat.category_id, label: cat.name }))],
      editable: true,
      renderCell: (params) => {
        const category = categories.find((cat: any) => cat.category_id === params.value);
        return category ? category.name : '';
      }
    },
    {
      field: 'line_total',
      headerName: 'Total',
      width: 110,
      editable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => {
        if (!row) return 0;
        return formatNumber((row.qty || 0) * (row.unit_price || 0), companyData.number_format, 2);
      },
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>
    },
    {
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: any }) => {
        const isInEditMode = voManualRowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<SaveIcon />} label="Save" color="primary" onClick={() => setVoManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }))} />,
            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={() => setVoManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }))} color="inherit" />
          ];
        }
        return [
          <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => setVoManualRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }))} color="inherit" />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => removeVoManualLine(String(id))} color="inherit" />
        ];
      }
    }
  ], [voManualLines, voManualRowModesModel, rateSheetItems, budgetLineItems, categories, companyData.number_format]);

  const handleVOFormChange = (evt) => {
    const { name, value } = evt.target;
    if (name === 'rate_sheet_id') {
      fetchRateSheetItems(value);
    }
    setVoFormValues(prev => ({ ...prev, [name]: value }));
  };

  // Fetch BOQ items for a specific DC
  const fetchVODCBoqItems = async (changeNumber: string) => {
    if (voDcBoqData[changeNumber]) return voDcBoqData[changeNumber]; // Already fetched

    setVoLoadingBoqData(true);
    try {
      const response = await fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`));
      if (!response.ok) throw new Error('Failed to fetch BOQ items');
      const data = await response.json();
      const items = Array.isArray(data) ? data : [];
      setVoDcBoqData(prev => ({ ...prev, [changeNumber]: items }));
      return items;
    } catch (err) {
      console.error('Failed to fetch DC BOQ items:', err);
      setVoDcBoqData(prev => ({ ...prev, [changeNumber]: [] }));
      return [];
    } finally {
      setVoLoadingBoqData(false);
    }
  };

  // Toggle DC expand/collapse
  const handleVOToggleDCExpand = (changeNumber: string) => {
    const isExpanded = voExpandedDCs.includes(changeNumber);
    if (isExpanded) {
      setVoExpandedDCs(prev => prev.filter(cn => cn !== changeNumber));
    } else {
      setVoExpandedDCs(prev => [...prev, changeNumber]);
      fetchVODCBoqItems(changeNumber); // Fetch BOQ items when expanding
    }
  };

  // Handle DC checkbox (parent) - selects/deselects all BOQ items
  const handleVODCSelect = (changeNumber: string) => {
    const boqItems = voDcBoqData[changeNumber] || [];
    const selectedItems = voSelectedBoqItems[changeNumber] || [];

    if (selectedItems.length === boqItems.length && boqItems.length > 0) {
      // All selected → deselect all
      setVoSelectedBoqItems(prev => ({ ...prev, [changeNumber]: [] }));
      if (dialogOpen && dialogMode === 'edit') {
        setEditManualLines(prev => prev.filter(l => l.source_change_number !== changeNumber));
      } else {
        // Create mode: remove DC lines from voManualLines
        setVoManualLines(prev => prev.filter(l => l.source_change_number !== changeNumber));
      }
    } else {
      // Show copy BOQ confirmation prompt
      setCopyBoqTargetDC(changeNumber);
      setCopyBoqConfirmOpen(true);
    }
  };

  const handleCopyBoqConfirm = async () => {
    const existingItems = voDcBoqData[copyBoqTargetDC];
    const items = existingItems && existingItems.length > 0
      ? existingItems
      : await fetchVODCBoqItems(copyBoqTargetDC);
    const allBoqIds = (items || []).map((item: any) => item.dc_boq_line_id);
    setVoSelectedBoqItems(prev => ({ ...prev, [copyBoqTargetDC]: allBoqIds }));
    setVoExpandedDCs(prev => prev.includes(copyBoqTargetDC) ? prev : [...prev, copyBoqTargetDC]);

    const targetDC = copyBoqTargetDC;

    // Helper to build new editable lines from BOQ items (skipping already-added ones)
    const buildNewBoqLines = (existingLines: any[]) => {
      const existingDcBoqIds = new Set(existingLines.filter(l => l.source_dc_boq_line_id).map(l => l.source_dc_boq_line_id));
      return (items || [])
        .filter((item: any) => !existingDcBoqIds.has(item.dc_boq_line_id))
        .map((item: any, i: number) => ({
          id: `ml_${Date.now()}_${i}_${item.dc_boq_line_id}`,
          description: item.item_description || '',
          qty: item.qty || 1,
          uom: item.unit_code || '',
          unit_price: item.unit_price || 0,
          category_id: '',
          source_change_number: targetDC,
          source_dc_boq_line_id: item.dc_boq_line_id
        }));
    };

    if (dialogOpen && dialogMode === 'edit') {
      // Edit mode: add to editManualLines
      setEditManualLines(prev => [...prev, ...buildNewBoqLines(prev)]);
    } else {
      // Create mode: add to voManualLines as editable rows
      setVoManualLines(prev => [...prev, ...buildNewBoqLines(prev)]);
    }

    setCopyBoqConfirmOpen(false);
    setCopyBoqTargetDC('');
  };

  const handleCopyBoqDecline = () => {
    setCopyBoqConfirmOpen(false);
    setCopyBoqTargetDC('');
  };

  // Handle individual BOQ item checkbox (child)
  const handleVOBoqItemSelect = (changeNumber: string, boqLineId: number) => {
    const currentItems = voSelectedBoqItems[changeNumber] || [];
    const isSelected = currentItems.includes(boqLineId);

    setVoSelectedBoqItems(prev => {
      if (isSelected) {
        return { ...prev, [changeNumber]: currentItems.filter(id => id !== boqLineId) };
      } else {
        return { ...prev, [changeNumber]: [...currentItems, boqLineId] };
      }
    });

    // Sync the lines state (edit or create mode)
    const boqItem = (voDcBoqData[changeNumber] || []).find((item: any) => item.dc_boq_line_id === boqLineId);
    if (dialogOpen && dialogMode === 'edit') {
      if (isSelected) {
        setEditManualLines(prev => prev.filter(l => l.source_dc_boq_line_id !== boqLineId));
      } else if (boqItem) {
        setEditManualLines(prev => {
          if (prev.some(l => l.source_dc_boq_line_id === boqLineId)) return prev;
          return [...prev, {
            id: `ml_${Date.now()}_${boqLineId}`,
            description: boqItem.item_description || '',
            qty: boqItem.qty || 1,
            uom: boqItem.unit_code || '',
            unit_price: boqItem.unit_price || 0,
            category_id: '',
            source_change_number: changeNumber,
            source_dc_boq_line_id: boqLineId
          }];
        });
      }
    } else {
      // Create mode: sync voManualLines
      if (isSelected) {
        setVoManualLines(prev => prev.filter(l => l.source_dc_boq_line_id !== boqLineId));
      } else if (boqItem) {
        setVoManualLines(prev => {
          if (prev.some(l => l.source_dc_boq_line_id === boqLineId)) return prev;
          return [...prev, {
            id: `ml_${Date.now()}_${boqLineId}`,
            description: boqItem.item_description || '',
            qty: boqItem.qty || 1,
            uom: boqItem.unit_code || '',
            unit_price: boqItem.unit_price || 0,
            category_id: '',
            source_change_number: changeNumber,
            source_dc_boq_line_id: boqLineId
          }];
        });
      }
    }
  };

  const handleCreateVOSubmit = async () => {
    if (!canWrite) {
      setVoDialogError('You do not have permission to create variation orders.');
      return;
    }
    const trimmedNumber = voFormValues.contract_number.trim();
    if (!trimmedNumber) {
      setVoDialogError('VO number is required.');
      return;
    }

    setCreatingVO(true);
    setVoDialogError('');

    // Create VO with basic fields only (no boq_items — lines saved via PUT after creation)
    const payload = {
      project_id: projectId,
      contract_number: trimmedNumber,
      title: voFormValues.title.trim() || null,
      original_value: '0',
      currency: voFormValues.currency || 'SGD',
      signed_date: voFormValues.signed_date || null,
      rate_sheet_id: voFormValues.rate_sheet_id || null,
      contract_type: 'variation_order',
      created_by: userId ? String(userId) : null
    };

    try {
      const response = await fetch(apiUrl(`/api/contracts`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create variation order');
      }

      // Save all lines (BOQ from DC + manual) + sync DC relationships
      const allLines = voManualLines
        .filter(l => l.description && String(l.description).trim())
        .map((l, idx) => ({
          line_no: idx + 1,
          description: l.description,
          qty: Number(l.qty) || 1,
          uom: l.uom || '',
          unit_price: Number(l.unit_price) || 0,
          category_id: l.category_id || null,
          source_change_number: l.source_change_number || null,
          source_dc_boq_line_id: l.source_dc_boq_line_id || null
        }));

      const changeNumbers = [...new Set(allLines.filter(l => l.source_change_number).map(l => l.source_change_number))];

      if (allLines.length > 0 || changeNumbers.length > 0) {
        const linesRes = await fetch(apiUrl(`/api/contracts/${data.contract_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
          body: JSON.stringify({
            project_id: projectId,
            contract_number: payload.contract_number,
            title: payload.title,
            original_value: '0',
            currency: payload.currency,
            contract_type: 'variation_order',
            change_numbers: changeNumbers,
            lines: allLines
          })
        });
        if (!linesRes.ok) {
          const linesErr = await linesRes.json();
          throw new Error(linesErr.error || 'Failed to save budget lines');
        }
      }

      const totalLines = allLines.length;
      setSnackbar({
        open: true,
        message: `Variation order created successfully${totalLines > 0 ? ` with ${totalLines} line item(s)` : ''}`,
        severity: 'success'
      });

      handleCloseVODialog();
      fetchContracts(); // Refresh to show new VO
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (err: any) {
      setVoDialogError(err.message);
    } finally {
      setCreatingVO(false);
    }
  };

  const handleFormChange = evt => {
    const { name, value } = evt.target;
    if (name === 'rate_sheet_id') {
      console.log('🔄 Rate sheet selection changed to:', value);
      fetchRateSheetItems(value);
    }
    setFormValues(prev => ({ ...prev, [name]: value }));
  };



  const preparePayload = () => {
    console.log('🔍 Preparing payload with formValues:', formValues);
    console.log('🔍 FormValues rate_sheet_id value:', formValues.rate_sheet_id);
    // NOTE: signed_date is intentionally not sent from this form. The manager
    // assigns it automatically when they approve the contract/VO, so the QS
    // team should never be able to set or overwrite it from the edit dialog.
    const payload: any = {
      project_id: projectId,
      contract_number: formValues.contract_number.trim(),
      title: formValues.title.trim() || null,
      original_value: formValues.original_value,
      currency: formValues.currency || 'SGD',
      rate_sheet_id: formValues.rate_sheet_id || null
    };

    // When editing, preserve parent_contract_id and contract_type (important for VOs)
    if (dialogMode === 'edit' && selectedContract) {
      if (selectedContract.parent_contract_id) {
        payload.parent_contract_id = selectedContract.parent_contract_id;
      }
      if (selectedContract.contract_type) {
        payload.contract_type = selectedContract.contract_type;
      }
    }

    console.log('📤 Final payload to send:', payload);
    console.log('📤 Payload rate_sheet_id value:', payload.rate_sheet_id);
    return payload;
  };

  const handleSubmit = (alsoSendForApproval = false) => {
    if (!canWrite) {
      setDialogError('You do not have permission to save contracts.');
      return;
    }
    if (!projectId) {
      setDialogError('Select a project before saving.');
      return;
    }
    const trimmedNumber = formValues.contract_number.trim();
    if (!trimmedNumber) {
      setDialogError('Contract number is required.');
      return;
    }
    if (formValues.original_value === '') {
      setDialogError('Original value is required.');
      return;
    }
    const payload = preparePayload();
    setSaving(true);
    const url = dialogMode === 'create'
      ? apiUrl('/api/contracts')
      : apiUrl(`/api/contracts/${selectedContract.contract_id}`);
    const method = dialogMode === 'create' ? 'POST' : 'PUT';
    console.log(`🚀 Making ${method} request to ${url} with payload:`, payload);
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        const data = await res.json();
        console.log('📥 Server response:', { status: res.status, data });
        if (!res.ok) throw new Error(data.error || 'Save failed');
        return data;
      })
      .then(async (data) => {
        console.log('✅ Contract saved with rate_sheet_id:', data.rate_sheet_id, 'rate_sheet_name:', data.rate_sheet_name);

        // In edit mode, persist BOQ lines (both VOs and Contracts) in a
        // second PUT. The endpoint accepts the same shape for both — Contracts
        // simply have null source_change_number / source_dc_boq_line_id.
        if (dialogMode === 'edit' && selectedContract) {
          const isVO = formValues.contract_type === 'variation_order';
          try {
            setBudgetLineLoading(true);

            // Build all lines from editManualLines (includes both BOQ-sourced and manual)
            const allLines = editManualLines
              .filter((l: any) => l.description && String(l.description).trim())
              .map((l: any, idx: number) => ({
                id: String(l.id).startsWith('ml_') ? undefined : l.id,
                line_no: idx + 1,
                description: l.description,
                qty: Number(l.qty) || 1,
                uom: l.uom || '',
                unit_price: Number(l.unit_price) || 0,
                category_id: l.category_id || null,
                source_change_number: l.source_change_number || null,
                source_dc_boq_line_id: l.source_dc_boq_line_id || null
              }));

            const linesRes = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
              body: JSON.stringify({
                project_id: projectId,
                contract_number: formValues.contract_number,
                title: formValues.title,
                original_value: formValues.original_value,
                currency: formValues.currency,
                contract_type: selectedContract.contract_type,
                lines: allLines
              })
            });

            setBudgetLineLoading(false);

            if (!linesRes.ok) {
              const linesErr = await linesRes.json();
              throw new Error(linesErr.error || 'Failed to save budget lines');
            }

            console.log(`✅ ${isVO ? 'VO' : 'Contract'} budget lines saved successfully`);
          } catch (boqErr: any) {
            console.error(`Failed to save ${isVO ? 'VO' : 'Contract'} budget lines:`, boqErr);
            setBudgetLineLoading(false);
            setSnackbar({ open: true, message: `${isVO ? 'VO' : 'Contract'} saved but lines update failed: ${boqErr.message}`, severity: 'warning' });
            setSaving(false);
            return;
          }
        }

        // Show appropriate message based on whether it's a VO or Contract
        const isVO = selectedContract?.contract_type === 'variation_order';
        const entityType = isVO ? 'VO' : 'Contract';
        const message = dialogMode === 'create' ? `${entityType} created.` : `${entityType} updated.`;
        setSnackbar({ open: true, message, severity: 'success' });

        // If the Save & Send for Approval button was clicked, transition status now
        if (alsoSendForApproval && selectedContract) {
          try {
            await handleSendForApproval(selectedContract);
          } catch (sendErr) {
            console.error('Failed to send for approval after save:', sendErr);
          }
        }

        // Clear focus from save button before closing dialog to prevent accessibility warning
        if (saveButtonRef.current && 'blur' in saveButtonRef.current) {
          (saveButtonRef.current as HTMLElement).blur();
        }
        // Small delay to ensure blur happens before dialog closes
        setTimeout(() => {
          handleCloseDialog();
          fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        }, 10);
      })
      .catch(err => {
        // Clear focus from save button on error to prevent accessibility issues
        if (saveButtonRef.current && 'blur' in saveButtonRef.current) {
          (saveButtonRef.current as HTMLElement).blur();
        }
        setDialogError(err.message);
        setSaving(false);
      });
  };

  // DataGrid handlers - define before useMemo
  const handleContractEditClick = useCallback((id) => {
    const contract = contracts.find(c => c.contract_id === id);
    if (contract) {
      if (contract.status === 'Approved') {
        setSnackbar({
          open: true,
          message: 'This contract is already approved and cannot be edited.',
          severity: 'warning'
        });
        return;
      }
      handleOpenEdit(contract);
    }
  }, [contracts, handleOpenEdit]);

  const handleContractSaveClick = useCallback((id) => {
    // TODO: Implement inline save
    console.log('Save contract:', id);
  }, []);

  const handleContractCancelClick = useCallback((id) => {
    // TODO: Implement inline cancel
    console.log('Cancel contract edit:', id);
  }, []);

  const handleContractDeleteClick = useCallback((id) => {
    const contract = contracts.find(c => c.contract_id === id);
    setItemToDelete(contract);
    setDeleteType('contract');
    setDeleteDialogOpen(true);
  }, [contracts]);

  // Handle contract/VO row click to select it
  const handleContractRowClick = useCallback(async (contract: any) => {
    // Select the contract/VO to show its budget lines
    setSelectedContract(contract);
  }, []);

  // VO-specific handlers
  const handleVODeleteClick = useCallback((vo: any) => {
    setItemToDelete(vo);
    setDeleteType('vo');
    setDeleteDialogOpen(true);
  }, []);

  const handleVOEditClick = useCallback((vo: any) => {
    if (vo?.status === 'Approved') {
      setSnackbar({
        open: true,
        message: 'This VO is already approved and cannot be edited.',
        severity: 'warning'
      });
      return;
    }
    // Use the same edit dialog as contracts - VOs are stored in the same table
    console.log('Opening edit for VO:', vo);
    handleOpenEdit(vo);
  }, [handleOpenEdit]);

  // Send for Approval
  const handleSendForApproval = useCallback(async (contract: any) => {
    if (!contract.contract_id) return;

    try {
      const response = await fetch(apiUrl(`/api/contracts/${contract.contract_id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ status: 'Pending Approval' })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send for approval');
      }

      setSnackbar({ open: true, message: `${contract.contract_type === 'variation_order' ? 'VO' : 'Contract'} sent for approval successfully`, severity: 'success' });
      fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error) {
      console.error('Error sending for approval:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to send for approval', severity: 'error' });
    }
  }, [fetchContracts]);

  // View Contract/VO Handler
  const handleViewContract = useCallback(async (contract: any) => {
    setSelectedViewContract(contract);
    setViewBOQLines([]);
    setViewBudgetSummary(null);
    setViewLinkedDCs([]);
    setViewDialogOpen(true);
    setViewBOQLoading(true);
    try {
      const boqRes = await fetch(apiUrl(`/api/contracts/${contract.contract_id}/budget-lines`), { credentials: 'include', headers: { ...(userId ? { 'X-User-ID': userId } : {}) } });
      if (boqRes.ok) {
        const data = await boqRes.json();
        setViewBOQLines(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setViewBOQLines([]);
    } finally {
      setViewBOQLoading(false);
    }
    fetch(apiUrl(`/api/contracts/${contract.contract_id}/budget-summary`), { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setViewBudgetSummary(data); })
      .catch(() => {});
    // Fetch linked design changes for VOs
    if (contract.contract_type === 'variation_order') {
      fetch(apiUrl(`/api/contract-design-changes?contract_id=${contract.contract_id}`), { credentials: 'include', headers: { ...(userId ? { 'X-User-ID': userId } : {}) } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setViewLinkedDCs(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [userId]);

  // Budget Line Management Functions
  const handleAddBudgetLine = useCallback(() => {
    if (!selectedContract) return;

    setBudgetLineLoading(true); // Show loading in budget lines DataGrid
    setLoading(true); // Show loading in main contracts table

    const newLine = {
      id: `temp_${Date.now()}`, // Temporary ID for new lines
      contract_id: selectedContract.contract_id,
      line_no: (selectedContract.lines?.length || 0) + 1,
      description: '',
      qty: 1,
      uom: '',
      unit_price: 0,
      category_id: '' // Use empty string instead of null for singleSelect compatibility
    };

    const updatedLines = [...(selectedContract.lines || []), newLine];
    setSelectedContract(prev => ({ ...prev, lines: updatedLines }));
    setBudgetLineRowModesModel(prev => ({ ...prev, [newLine.id]: { mode: GridRowModes.Edit } }));

    // Hide loading indicators after state updates
    setTimeout(() => {
      setBudgetLineLoading(false);
      setLoading(false);
    }, 100);
  }, [selectedContract]);

  const handleBudgetLineEditClick = useCallback((id) => {
    setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleBudgetLineSaveClick = useCallback(async (id) => {
    try {
      console.log('💾 Save clicked for ID:', id);
      console.log('🔍 selectedContract:', selectedContract);
      console.log('🔍 selectedContract.lines:', selectedContract?.lines);
      
      // Early validation - but allow empty lines array
      if (!selectedContract) {
        console.error('No contract selected for saving');
        setSnackbar({ open: true, message: 'Error: No contract selected', severity: 'error' });
        return;
      }

      // Exit edit mode first - this should trigger DataGrid to commit any pending edits
      setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
      
      // Add a small delay to allow processRowUpdate to complete and update state first
      // This prevents race condition where manual save uses stale data
      setTimeout(() => {
        setPendingSaveId(id);
      }, 100); // Wait for processRowUpdate to finish
      
    } catch (error) {
      console.error('Error in save click handler:', error);
      setSnackbar({ open: true, message: 'Failed to save budget line', severity: 'error' });
    }
  }, [selectedContract]);

  const handleBudgetLineCancelClick = useCallback((id) => {
    setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    
    // If it's a new line being cancelled, remove it
    if (String(id).startsWith('temp_')) {
      setSelectedContract(prev => ({
        ...prev,
        lines: prev.lines.filter(line => line.id !== id)
      }));
    }
  }, []);

  const handleBudgetLineDeleteClick = useCallback((id) => {
    // Early validation
    if (!selectedContract) {
      console.error('No contract selected for deletion');
      setSnackbar({ open: true, message: 'Error: No contract selected', severity: 'error' });
      return;
    }

    // Open delete dialog
    const lineToDelete = selectedContract.lines?.find(line => line.id === id);
    setItemToDelete({ ...lineToDelete, budgetLineId: id });
    setDeleteType('budgetLine');
    setDeleteDialogOpen(true);
  }, [selectedContract]);

  // Actual delete logic moved to confirm handler
  const confirmDeleteBudgetLine = useCallback(async (id) => {
    if (!selectedContract) return;

    // If it's a temp line, just remove from state
    if (String(id).startsWith('temp_')) {
      setSelectedContract(prev => {
        if (!prev || !prev.lines) return prev;
        return {
          ...prev,
          lines: prev.lines.filter(line => line.id !== id)
        };
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    // Check if selectedContract and lines exist
    if (!selectedContract || !selectedContract.lines) {
      console.error('No contract or lines available for deletion');
      setSnackbar({ open: true, message: 'Error: No contract selected or no lines available', severity: 'error' });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    try {
      setBudgetLineLoading(true); // Show loading state
      setLoading(true); // Show loading in main contracts table
      setDeleting(true);

        // Update the contract by removing the line and sending updated contract
        const remainingLines = selectedContract.lines.filter(line => line.id !== id);
        const linesToSave = remainingLines.map(line => {
          // Handle category_id properly - only send if it's a valid number
          let categoryId = line.category_id;
          if (categoryId === '' || categoryId === null || categoryId === undefined) {
            categoryId = null;
          }

          return {
            id: String(line.id).startsWith('temp_') ? undefined : line.id,
            line_no: line.line_no,
            description: line.description,
            qty: line.qty,
            uom: line.uom,
            unit_price: line.unit_price,
            category_id: categoryId,
            // Preserve source DC info if present
            source_change_number: line.source_change_number || undefined,
            source_dc_boq_line_id: line.source_dc_boq_line_id || undefined
          };
        });

        const payload: any = {
          contract_number: selectedContract.contract_number,
          title: selectedContract.title,
          original_value: selectedContract.original_value,
          currency: selectedContract.currency,
          signed_date: selectedContract.signed_date,
          rate_sheet_id: selectedContract.rate_sheet_id,
          lines: linesToSave
        };

        // Preserve VO-specific fields if this is a VO
        if (selectedContract.contract_type) {
          payload.contract_type = selectedContract.contract_type;
        }
        if (selectedContract.parent_contract_id) {
          payload.parent_contract_id = selectedContract.parent_contract_id;
        }

        const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const updatedContract = await response.json();
          setSelectedContract(updatedContract);

          // Refetch all contracts to update total values immediately
          await fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));

          setBudgetLineLoading(false); // Hide loading state
          setLoading(false); // Hide loading in main contracts table
          setSnackbar({ open: true, message: 'Budget line deleted successfully', severity: 'success' });
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        } else {
          setBudgetLineLoading(false); // Hide loading state on error
          setLoading(false); // Hide loading in main contracts table on error
          const error = await response.json();
          console.error('❌ Failed to delete budget line. Status:', response.status, 'Error:', error);
          setSnackbar({ open: true, message: error.error || 'Failed to delete budget line', severity: 'error' });
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }
      } catch (error) {
        setBudgetLineLoading(false); // Hide loading state on error
        setLoading(false); // Hide loading in main contracts table on error
        console.error('❌ Error deleting budget line:', error);
        setSnackbar({ open: true, message: 'Failed to delete budget line', severity: 'error' });
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } finally {
        setDeleting(false);
      }
  }, [selectedContract, userId, fetchContracts]);

  const handleBudgetLineRateSheetItemSelect = useCallback((lineId, rateSheetItem) => {
    if (rateSheetItem) {
      console.log('📋 Budget line selected rate sheet item:', rateSheetItem);
      console.log('🔍 Available categories:', categories);
      console.log('🔍 Looking for category_name:', rateSheetItem.category_name);
      
      // Find the category_id based on category_name with better matching
      const category = categories.find(cat => 
        cat.name === rateSheetItem.category_name || 
        cat.name?.toLowerCase() === rateSheetItem.category_name?.toLowerCase()
      );
      
      // Use empty string instead of null for singleSelect compatibility
      const categoryId = category ? category.category_id : '';
      console.log('🏷️ Auto-populating category:', rateSheetItem.category_name, '→', categoryId, category ? '✅' : '❌');
      
      setSelectedContract(prev => {
        const lineExists = prev.lines.some(line => line.id === lineId);
        console.log('🔍 Line exists in state:', lineExists, 'lineId:', lineId);
        
        if (!lineExists) {
          console.log('⚠️ Line not found in state, might be a new line in edit mode');
          return prev; // Don't update if line doesn't exist yet
        }
        
        return {
          ...prev,
          lines: prev.lines.map(line => 
            line.id === lineId 
              ? { 
                  ...line, 
                  description: rateSheetItem.item_description,
                  unit_price: rateSheetItem.unit_price || 0,
                  uom: rateSheetItem.unit_code || '',
                  category_id: categoryId,
                  rsi_id: rateSheetItem.rsi_id,
                  bli_id: rateSheetItem.bli_id
                } 
              : line
          )
        };
      });
    }
  }, [categories]);

  // Process row update for contracts (main DataGrid)
  const processContractRowUpdate = useCallback(async (newRow) => {
    console.log('🔄 processContractRowUpdate called with:', newRow);
    
    if (!canWrite) {
      throw new Error('You do not have permission to edit contracts.');
    }
    
    try {
      // Prepare the payload for the API call
      const payload = {
        project_id: projectId,
        contract_number: newRow.contract_number?.trim(),
        title: newRow.title?.trim() || null,
        original_value: newRow.original_value,
        currency: newRow.currency || 'SGD',
        signed_date: newRow.signed_date || null,
        rate_sheet_id: newRow.rate_sheet_id || null
      };
      
      console.log('📅 Date handling - newRow.signed_date:', newRow.signed_date, typeof newRow.signed_date);
      
      console.log('🚀 Updating contract via API:', payload);

      const response = await fetch(apiUrl(`/api/contracts/${newRow.contract_id}`), {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update contract');
      }
      
      const updatedContract = await response.json();
      console.log('✅ Contract updated successfully:', updatedContract);
      
      // Update local state
      setContracts(prev => prev.map(contract => 
        contract.contract_id === newRow.contract_id 
          ? { ...contract, ...updatedContract }
          : contract
      ));
      
      // Update selected contract if it's the one being edited
      if (selectedContract?.contract_id === newRow.contract_id) {
        setSelectedContract(prev => ({ ...prev, ...updatedContract }));
      }
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Contract updated successfully',
        severity: 'success'
      });
      
      return { ...newRow, ...updatedContract };
      
    } catch (error) {
      console.error('❌ Error updating contract:', error);
      setSnackbar({
        open: true,
        message: `Error updating contract: ${error.message}`,
        severity: 'error'
      });
      throw error; // This will revert the row to its original state
    }
  }, [canWrite, projectId, userId, selectedContract?.contract_id]);

  const budgetLineColumns: GridColDef[] = useMemo(() => [
    { field: 'line_no', headerName: 'Line #', width: 80, editable: canWrite, renderCell: (params) => {
      if (params.value !== null && params.value !== undefined && params.value !== '') return params.value;
      const rows = params.api.getSortedRowIds();
      return rows.indexOf(params.id) + 1;
    }},
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 300, 
      editable: canWrite,
      renderEditCell: (params) => {
        // Use rate sheet items if available, otherwise use budget line items
        const itemsToShow = rateSheetItems.length > 0 ? rateSheetItems : budgetLineItems;

        return (
          <Autocomplete
            fullWidth
            size="small"
            options={itemsToShow}
            getOptionLabel={(option) => option.item_description || ''}
            value={itemsToShow.find(item => item.item_description === params.value) || null}
            noOptionsText={itemsToShow.length === 0 ? "No budget/rate items available" : "No matching items found"}
            onChange={(event, newValue) => {
              console.log('🎯 Autocomplete onChange triggered:', { lineId: params.id, newValue });
              if (newValue) {
                // Find the category_id based on category_name
                const category = categories.find(cat => 
                  cat.name === newValue.category_name || 
                  cat.name?.toLowerCase() === newValue.category_name?.toLowerCase()
                );
                const categoryId = category ? category.category_id : '';
                
                console.log('🔄 Updating fields via DataGrid API:', {
                  description: newValue.item_description,
                  unit_price: newValue.unit_price || 0,
                  uom: newValue.unit_code || '',
                  category_id: categoryId
                });
                
                // Immediately update the description field
                params.api.setEditCellValue({ id: params.id, field: 'description', value: newValue.item_description || '' });
                
                // Update other fields with a slight delay to ensure proper sequencing
                setTimeout(() => {
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newValue.unit_price || 0 });
                    console.log('Updated unit_price to:', newValue.unit_price);
                  } catch (e) { console.error('Error setting unit_price:', e); }
                  
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'uom', value: newValue.unit_code || '' });
                    console.log('Updated uom to:', newValue.unit_code);
                  } catch (e) { console.error('Error setting uom:', e); }
                  
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'category_id', value: categoryId });
                    console.log('Updated category_id to:', categoryId);
                  } catch (e) { console.error('Error setting category_id:', e); }
                }, 100);
                
                // Also update the state for persistence
                handleBudgetLineRateSheetItemSelect(params.id, newValue);
              } else {
                // If cleared, just update description
                console.log('🔄 Clearing description field');
                params.api.setEditCellValue({ id: params.id, field: 'description', value: '' });
              }
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                variant="standard"
                placeholder="Search and select item..."
                InputProps={{
                  ...inputParams.InputProps,
                  disableUnderline: true,
                  sx: { fontSize: '0.875rem' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.item_description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.category_name} • {option.unit_code} • ${option.unit_price}
                  </Typography>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              return options.filter(option =>
                option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
          />
        );
      }
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 100,
      type: 'number',
      editable: canWrite
    },
    { field: 'uom', headerName: 'UOM', width: 100, editable: canWrite },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 130,
      type: 'number',
      editable: canWrite
    },
    { 
      field: 'category_id', 
      headerName: 'Category', 
      width: 200,
      type: 'singleSelect',
      valueOptions: [
        { value: '', label: '-- Select Category --' },
        ...categories.map(cat => ({ value: cat.category_id, label: cat.name }))
      ],
      editable: canWrite,
      renderCell: (params) => {
        if (!params.value) return '';
        const category = categories.find(cat => cat.category_id === params.value);
        return category ? category.name : '';
      }
    },
    {
      field: 'line_total',
      headerName: 'Line Total',
      width: 130,
      type: 'number',
      editable: false,
      valueGetter: (value, row) => {
        if (!row) return formatNumber(0, companyData.number_format, 2);
        // Always calculate from qty and unit_price for real-time updates
        const qty = row.qty || 0;
        const price = row.unit_price || 0;
        return formatNumber(qty * price, companyData.number_format, 2);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'pm_comment',
      headerName: 'PM Comment',
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5, py: 0.5, fontSize: '0.85rem' }}>
          {params.value || ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = budgetLineRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={() => handleBudgetLineSaveClick(id)}
              showInMenu={false}
              key="save"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={() => handleBudgetLineCancelClick(id)}
              color="inherit"
              showInMenu={false}
              key="cancel"
            />,
          ];
        }

        return [
          ...(canWrite ? [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={() => handleBudgetLineEditClick(id)}
              color="inherit"
              showInMenu={false}
              key="edit"
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleBudgetLineDeleteClick(id)}
              showInMenu={false}
              key="delete"
            />,
          ] : []),
        ];
      },
    },
  ], [canWrite, rateSheetItems, categories, budgetLineRowModesModel, handleBudgetLineRateSheetItemSelect, handleBudgetLineEditClick, handleBudgetLineSaveClick, handleBudgetLineCancelClick, handleBudgetLineDeleteClick]) as GridColDef[];

  // Flatten contracts and VOs for display (VOs shown as sub-rows when contract is expanded)
  const flattenedRows = useMemo(() => {
    // Return all contracts and VOs at the same level (no subrows)
    return contracts.map((contract) => ({
      ...contract,
      rowType: contract.contract_type === 'variation_order' ? 'vo' : 'contract'
    }));
  }, [contracts]);

  const contractColumns: any[] = useMemo(() => [
    {
      field: 'contract_number',
      headerName: 'Contract Number',
      width: 200,
      editable: !canWrite ? false : true,
      renderCell: (params) => {
        return (
          <Typography variant="body2">
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'contract_type',
      headerName: 'Type',
      width: 120,
      editable: false,
      renderCell: (params) => {
        const isVO = params.value === 'variation_order';
        return (
          <Chip
            label={isVO ? 'VO' : 'Contract'}
            size="small"
            color={isVO ? 'secondary' : 'primary'}
            variant="outlined"
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 300,
      editable: !canWrite ? false : true,
    },
    {
      field: 'original_value',
      headerName: 'Original Contract Value',
      width: 170,
      editable: (params) => {
        // VOs cannot edit original value
        if (params.row && params.row.contract_type === 'variation_order') return false;
        return canWrite;
      },
      type: 'number',
      valueGetter: (value, row) => {
        return formatCurrency(value || 0, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    // BOQ Value column - shows total value of all BOQ items (qty * unit_price)
    {
      field: 'boq_value',
      headerName: 'Original BOQ Value',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        if (!row || !row.lines) return formatCurrency(0, row.currency || 'SGD', companyData.number_format);
        // Calculate total of all budget line items (qty * unit_price)
        const total = row.lines.reduce((sum, line) => {
          if (!line) return sum;
          const qty = line.qty || 0;
          const price = line.unit_price || 0;
          return sum + (qty * price);
        }, 0);
        return formatCurrency(total, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    // Approved Budget column - shows budget assigned during approval
    {
      field: 'budget',
      headerName: 'Approved Budget',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        // Only show approved budget if status is Approved
        if (row && row.status === 'Approved' && value != null) {
          return formatCurrency(value, row.currency || 'SGD', companyData.number_format);
        }
        return 'N/A';
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'spent',
      headerName: 'Spent (MR/PO)',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        const spent = (row.total_value || 0) - (row.remaining_balance || 0);
        return formatCurrency(spent, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'remaining_balance',
      headerName: 'Budget Balance',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        const budget = row.budget;
        if (budget == null) return 'N/A';
        const spent = (row.total_value || 0) - (row.remaining_balance || 0);
        return formatCurrency(budget - spent, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 100,
      editable: !canWrite ? false : true,
      type: 'singleSelect',
      valueOptions: ['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'],
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      editable: false,
      renderCell: (params) => {
        const status = params.value || 'Draft';
        let color: 'default' | 'warning' | 'success' | 'error' = 'default';

        if (status === 'Draft') {
          color = 'default';
        } else if (status === 'Pending Approval') {
          color = 'warning';
        } else if (status === 'Approved') {
          color = 'success';
        } else if (status === 'Rejected') {
          color = 'error';
        }

        return (
          <Tooltip title={status}>
            <Chip
              label={status}
              size="small"
              color={color}
              variant="outlined"
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        );
      },
    },
    // HIDDEN: Signed Date column (to make room for action icons)
    /* {
      field: 'signed_date',
      headerName: 'Signed Date',
      width: 120,
      editable: !canWrite ? false : true,
      type: 'date',
      valueGetter: (value: any) => {
        if (!value) return null;
        try {
          // Handle both Date objects and date strings
          if (value instanceof Date) {
            return value;
          }
          // If it's a string, parse it as a local date to avoid timezone issues
          const dateStr = (value as any).toString();
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            // ISO string - convert to local date
            const date = new Date(dateStr);
            return new Date(date.getFullYear(), date.getMonth(), date.getDate());
          } else {
            // Assume local date string (YYYY-MM-DD format)
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          }
        } catch (error) {
          console.error('Error parsing date:', value, error);
          return null;
        }
      },
      valueSetter: (value, row) => {
        if (!value) return { ...row, signed_date: null };
        try {
          // Ensure we store the date as YYYY-MM-DD string to avoid timezone issues
          let dateToSet;
          if (value instanceof Date) {
            dateToSet = value;
          } else {
            dateToSet = new Date(value);
          }

          if (isNaN(dateToSet.getTime())) {
            return { ...row, signed_date: null };
          }

          // Format as YYYY-MM-DD to avoid timezone conversion issues
          const year = dateToSet.getFullYear();
          const month = String(dateToSet.getMonth() + 1).padStart(2, '0');
          const day = String(dateToSet.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;

          return { ...row, signed_date: formattedDate };
        } catch (error) {
          console.error('Error setting date:', value, error);
          return { ...row, signed_date: null };
        }
      },
      valueFormatter: (value: any) => {
        if (!value) return '';
        try {
          let date;
          if (value instanceof Date) {
            date = value;
          } else {
            // Parse as local date to avoid timezone issues
            const dateStr = (value as any).toString();
            if (dateStr.includes('-')) {
              const [year, month, day] = dateStr.split('-').map(Number);
              date = new Date(year, month - 1, day);
            } else {
              date = new Date(value);
            }
          }

          if (isNaN(date.getTime())) return '';

          // Format as YYYY/MM/DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        } catch (error) {
          console.error('Error formatting date:', value, error);
          return '';
        }
      },
    }, */
    // Rate Sheet column - shows the name of the rate sheet selected for the contract/VO
    {
      field: 'rate_sheet_name',
      headerName: 'Rate Sheet',
      width: 180,
      editable: false,
      valueGetter: (value, row) => {
        return value || 'N/A';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 220,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = contractRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={() => handleContractSaveClick(id)}
              showInMenu={false}
              key="save"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={() => handleContractCancelClick(id)}
              showInMenu={false}
              key="cancel"
            />,
          ];
        }

        const isVO = row.rowType === 'vo';
        const count = row.document_count || 0;

        return [
          <GridActionsCellItem
            icon={<Tooltip title="View Details"><VisibilityIcon /></Tooltip>}
            label="View Details"
            onClick={(e) => {
              e.stopPropagation();
              handleViewContract(row);
            }}
            showInMenu={false}
            key="view"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Documents">
                <Badge badgeContent={count} color={count > 0 ? 'primary' : 'default'} max={99}>
                  <AttachFileIcon />
                </Badge>
              </Tooltip>
            }
            label="Documents"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedContractForDocs(row);
              setDocumentsDialogOpen(true);
            }}
            showInMenu={false}
            key="documents"
          />,
          ...(canWrite && (row.status === 'Draft' || row.status === 'Rejected') ? [
            <GridActionsCellItem
              icon={<Tooltip title="Send for Approval"><SendIcon /></Tooltip>}
              label="Send for Approval"
              onClick={(e) => {
                e.stopPropagation();
                handleSendForApproval(row);
              }}
              color="success"
              showInMenu={false}
              key="send"
            />,
          ] : []),
          ...(canWrite && row.status !== 'Approved' ? [
            <GridActionsCellItem
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                isVO ? handleVOEditClick(row) : handleContractEditClick(id);
              }}
              showInMenu={false}
              key="edit"
            />,
          ] : []),
          ...(canWrite ? [
            <GridActionsCellItem
              icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                isVO ? handleVODeleteClick(row) : handleContractDeleteClick(id);
              }}
              showInMenu={false}
              key="delete"
            />,
          ] : []),
        ];
      },
    },
  ], [contractRowModesModel, canWrite, contracts, handleContractEditClick, handleContractSaveClick, handleContractCancelClick, handleContractDeleteClick, handleContractRowClick, handleVOEditClick, handleVODeleteClick, handleSendForApproval, handleViewContract]);

  // Unified delete confirmation handler
  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    setDeleting(true);

    if (deleteType === 'contract') {
      try {
        const response = await fetch(apiUrl(`/api/contracts/${itemToDelete.contract_id}`), {
          method: 'DELETE',
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        });
        if (response.ok) {
          setContracts(prev => prev.filter(contract => contract.contract_id !== itemToDelete.contract_id));
          setSelectedContract(prev => prev?.contract_id === itemToDelete.contract_id ? null : prev);
          setSnackbar({ open: true, message: 'Contract deleted successfully', severity: 'success' });
          fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to delete contract', severity: 'error' });
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete contract', severity: 'error' });
      }
    } else if (deleteType === 'vo') {
      try {
        const response = await fetch(apiUrl(`/api/contracts/${itemToDelete.contract_id}`), {
          method: 'DELETE',
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        });
        if (response.ok) {
          setSelectedContract(prev => prev?.contract_id === itemToDelete.contract_id ? null : prev);
          setSnackbar({ open: true, message: 'VO deleted successfully', severity: 'success' });
          fetchContracts(); // Refresh all contracts/VOs
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to delete VO', severity: 'error' });
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete VO', severity: 'error' });
      }
    } else if (deleteType === 'budgetLine') {
      await confirmDeleteBudgetLine(itemToDelete.budgetLineId);
    }

    setDeleting(false);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, [itemToDelete, deleteType, userId, fetchContracts, confirmDeleteBudgetLine]);

  // Delete All handlers
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    if (!projectId) return;

    console.log('🔍 Delete All - userId:', userId);
    setDeletingAll(true);
    try {
      const response = await fetch(apiUrl(`/api/contracts/delete-all?project_id=${projectId}`), {
        method: 'DELETE',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
      });

      if (response.ok) {
        fetchContracts();
        window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        setSelectedContract(null);
        setSnackbar({ open: true, message: 'All contracts deleted successfully', severity: 'success' });
        setDeleteAllDialogOpen(false);
      } else {
        const error = await response.json();
        setSnackbar({ open: true, message: error.error || 'Failed to delete all contracts', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete all contracts', severity: 'error' });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  if (access === 'none') {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography>You do not have access to view contracts.</Typography>
      </Paper>
    );
  }

  if (!projectId) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography>Select a project to manage contracts.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '0 24px 16px 24px' }}>
        {/* Add Contract / VO Button */}
        {canWrite && (
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddMenu}>
              Add Contract / VO
            </Button>
            <Menu
              anchorEl={addMenuAnchorEl}
              open={addMenuOpen}
              onClose={handleCloseAddMenu}
            >
              <MenuItem onClick={handleOpenCreate}>Create Contract</MenuItem>
              <MenuItem onClick={handleOpenCreateVOFromMenu}>Create VO</MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Contracts Section */}
      {pageError && (
        <Alert severity="error" sx={{ margin: '0 24px 16px 24px' }}>{pageError}</Alert>
      )}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', background: 'white', margin: '0 24px', height: 'calc(100vh - 260px)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">Loading contracts...</Typography>
          </Box>
        ) : contracts.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
            <Typography variant="body1" color="text.secondary">No contracts recorded yet.</Typography>
          </Box>
        ) : (
          <DataGrid
                rows={flattenedRows}
                columns={contractColumns}
                getRowId={(row) => row.contract_id}
                loading={loading}
                onRowClick={(params) => {
                  console.log('🔍 Row selected:', params.row);
                  // Don't handle clicks on loading rows
                  if (params.row && params.row.rowType === 'loading') {
                    return;
                  }
                  if (params.row) {
                    // Handle both contracts and VOs
                    handleContractRowClick(params.row);

                    // Fetch rate sheet items if available
                    // For VOs without rate sheet, try parent contract's rate sheet
                    let rateSheetId = params.row.rate_sheet_id;
                    if (!rateSheetId && params.row.contract_type === 'variation_order' && params.row.parent_contract_id) {
                      const parentContract = contracts.find(c => c.contract_id === params.row.parent_contract_id);
                      if (parentContract && parentContract.rate_sheet_id) {
                        rateSheetId = parentContract.rate_sheet_id;
                        console.log('📋 Using parent contract rate sheet:', rateSheetId);
                      }
                    }

                    if (rateSheetId) {
                      fetchRateSheetItems(rateSheetId);
                    } else {
                      console.log('⚠️ No rate sheet found - budget line items will be used');
                      setRateSheetItems([]);
                    }
                  } else {
                    setSelectedContract(null);
                    setRateSheetItems([]);
                  }
                }}
                onRowDoubleClick={(params) => {
                  if (params.row && params.row.rowType !== 'loading') {
                    handleViewContract(params.row);
                  }
                }}
                isCellEditable={(params) => params.row.rowType !== 'loading'}
                processRowUpdate={processContractRowUpdate}
                onProcessRowUpdateError={(error) => {
                  console.error('❌ DataGrid processRowUpdate error:', error);
                  setSnackbar({
                    open: true,
                    message: `Error updating contract: ${error.message}`,
                    severity: 'error'
                  });
                }}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 50 }
                  }
                }}
                pageSizeOptions={[25, 50, 100]}
                rowSelectionModel={selectedContract ? { type: 'include', ids: new Set([selectedContract.contract_id]) } : { type: 'include', ids: new Set() }}
                sx={{ ...dataGridSx, flex: 1, minHeight: 0 }}
              />
        )}
      </Box>

      {/* Import/Export Buttons */}
      <div style={{ display: 'flex', gap: 8, margin: '0 24px 16px 24px' }}>
        <Button size="small" variant="outlined" onClick={handleDownloadTemplate}>Export Template</Button>
        <Button size="small" variant="contained" onClick={handleExportExcel} disabled={excelExporting || contracts.length === 0}>
          {excelExporting ? 'Exporting...' : 'Export To Excel'}
        </Button>
        <Button size="small" variant="contained" onClick={handleImportClick} disabled={excelImporting || !canWrite}>
          {excelImporting ? 'Importing...' : 'Import From Excel'}
        </Button>
        {canDeleteAll && (
          <Button size="small" variant="contained" color="error" onClick={handleDeleteAllClick} disabled={deletingAll || loading}>
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; if (fileInputRef.current) fileInputRef.current.value = ''; await runImport(f); }}
          style={{ display: 'none' }}
        />
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        disableRestoreFocus={true}
        TransitionProps={{
          onExited: () => {
            // Ensure focus is cleared after dialog animation completes
            if (document.activeElement && document.activeElement !== document.body && 'blur' in document.activeElement) {
              (document.activeElement as HTMLElement).blur();
            }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700}>
              {dialogMode === 'create'
                ? (formValues.contract_type === 'variation_order' ? 'Create Variation Order' : 'Create Contract')
                : (formValues.contract_type === 'variation_order' ? 'Edit Variation Order' : 'Edit Contract')}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <Box className="doc-preview" sx={{ background: 'white', color: '#222', p: 3 }}>

            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>
            )}

            {/* Company Header — mirrors the Preview dialog so Edit shares the
                same visual frame, with editable fields below. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2.5, borderBottom: '2px solid #e0e0e0' }}>
              {companyData.logo_url && (
                <Box component="img" src={companyData.logo_url} alt="logo"
                  sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{companyData.company_name || '—'}</Typography>
                {companyData.address && <Typography variant="body2" color="text.secondary">{companyData.address}</Typography>}
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  {companyData.phone && <Typography variant="body2"><strong>Tel:</strong> {companyData.phone}</Typography>}
                  {companyData.email && <Typography variant="body2"><strong>Email:</strong> {companyData.email}</Typography>}
                </Box>
              </Box>
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                  {formValues.contract_type === 'variation_order' ? 'VARIATION ORDER' : 'CONTRACT'}
                </Typography>
                {dialogMode === 'edit' && selectedContract && (
                  <Chip
                    label={selectedContract.status || 'Draft'}
                    size="small"
                    sx={{
                      mt: 0.5, fontWeight: 600,
                      bgcolor: selectedContract.status === 'Approved' ? '#e8f5e9' : selectedContract.status === 'Rejected' ? '#ffebee' : selectedContract.status === 'Pending Approval' ? '#fff8e1' : '#e3f2fd',
                      color: selectedContract.status === 'Approved' ? '#2e7d32' : selectedContract.status === 'Rejected' ? '#c62828' : selectedContract.status === 'Pending Approval' ? '#e65100' : '#1565c0',
                      border: `1px solid ${selectedContract.status === 'Approved' ? '#a5d6a7' : selectedContract.status === 'Rejected' ? '#ef9a9a' : selectedContract.status === 'Pending Approval' ? '#ffcc80' : '#90caf9'}`
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Rejection Comments Banner (edit mode) */}
            {dialogMode === 'edit' && formValues.rejection_comments && (
              <Box sx={{
                p: 2, mb: 3, bgcolor: '#fff5f5',
                border: '1px solid #ffcdd2', borderLeft: '4px solid #f44336',
                borderRadius: 1, display: 'flex', gap: 1.5, alignItems: 'flex-start'
              }}>
                <Typography sx={{ color: '#f44336', fontWeight: 'bold', fontSize: 18, lineHeight: 1.2 }}>⚠</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>
                    Manager Rejection Comments
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {formValues.rejection_comments}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Editable field grid — 3 columns, preview labels above standard
                (underline) inputs. Read-only computed fields use plain text.
                Signed Date stays read-only — it's set on manager approval. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2.5, mb: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                  Contract Number *
                </Typography>
                <TextField
                  required
                  fullWidth
                  variant="standard"
                  name="contract_number"
                  value={formValues.contract_number}
                  onChange={handleFormChange}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600, py: 0.3, mt: 0.3 } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                  Name / Title
                </Typography>
                <TextField
                  fullWidth
                  variant="standard"
                  name="title"
                  value={formValues.title}
                  onChange={handleFormChange}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600, py: 0.3, mt: 0.3 } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                  Currency
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  name="currency"
                  value={formValues.currency}
                  onChange={handleFormChange}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600, py: 0.3, mt: 0.3 } }}
                >
                  {['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'].map(code => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                  {formValues.contract_type === 'variation_order' ? 'Original VO Value *' : 'Original Contract Value *'}
                </Typography>
                <TextField
                  required
                  fullWidth
                  variant="standard"
                  type="number"
                  name="original_value"
                  value={formValues.original_value}
                  onChange={handleFormChange}
                  inputProps={{ step: '0.01' }}
                  helperText={formValues.contract_type === 'variation_order' ? 'Match this to the Total BOQ Cost shown below' : ''}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600, py: 0.3, mt: 0.3 } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.3 } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                  Rate Sheet
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  name="rate_sheet_id"
                  value={formValues.rate_sheet_id || ''}
                  onChange={handleFormChange}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600, py: 0.3, mt: 0.3 } }}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {rateSheets.map(sheet => (
                    <MenuItem key={sheet.rate_sheet_id} value={sheet.rate_sheet_id}>
                      {sheet.sheet_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {formValues.contract_type === 'variation_order' && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                    Total BOQ Cost
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 0.7, py: 0.3 }}>
                    {formatCurrency(
                      editManualLines.reduce((s: number, r: any) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0),
                      formValues.currency || 'SGD',
                      companyData.number_format
                    )}
                  </Typography>
                </Box>
              )}
              {dialogMode === 'edit' && selectedContract && (
                <>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                      Created Date
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.7, py: 0.3 }}>
                      {selectedContract.created_at ? new Date(selectedContract.created_at).toLocaleDateString() : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                      Signed Date
                    </Typography>
                    {selectedContract.status === 'Approved' && selectedContract.signed_date ? (
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.7, py: 0.3 }}>
                        {new Date(selectedContract.signed_date).toLocaleDateString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.7, py: 0.3, color: 'text.disabled', fontStyle: 'italic' }}>
                        Not yet signed (set on approval)
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>

            {/* DC/BOQ Selection for VOs in Edit Mode */}
            {dialogMode === 'edit' && formValues.contract_type === 'variation_order' && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Manage Design Changes in this VO
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Search and add design changes. Uncheck all BOQ items from a DC to unlink it.
                </Typography>
                <Autocomplete
                  options={designChanges.filter((dc: any) => !Object.keys(voSelectedBoqItems).includes(dc.change_number))}
                  getOptionLabel={(opt: any) => `${opt.change_number}${opt.change_name ? ' — ' + opt.change_name : ''}`}
                  value={null}
                  onChange={(_, dc: any) => {
                    if (!dc) return;
                    setVoSelectedBoqItems(prev => ({ ...prev, [dc.change_number]: [] }));
                    setVoExpandedDCs(prev => [...prev, dc.change_number]);
                    fetchVODCBoqItems(dc.change_number);
                  }}
                  isOptionEqualToValue={(opt: any, val: any) => opt.change_number === val.change_number}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Design Change"
                      placeholder="Type to search by number or name..."
                      size="small"
                    />
                  )}
                  noOptionsText={designChanges.length === 0 ? 'No design changes available' : 'All available design changes already added'}
                  sx={{ mb: 2 }}
                />
                {Object.keys(voSelectedBoqItems).length > 0 && (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    {Object.keys(voSelectedBoqItems).map((changeNumber) => {
                      const dc = designChanges.find((d: any) => d.change_number === changeNumber);
                      const isExpanded = voExpandedDCs.includes(changeNumber);
                      const boqItems = voDcBoqData[changeNumber] || [];
                      const selectedItems = voSelectedBoqItems[changeNumber] || [];
                      const allSelected = boqItems.length > 0 && selectedItems.length === boqItems.length;
                      const someSelected = selectedItems.length > 0 && selectedItems.length < boqItems.length;
                      return (
                        <Box key={changeNumber} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: isExpanded ? 'action.hover' : 'transparent', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onClick={() => handleVOToggleDCExpand(changeNumber)}
                          >
                            <IconButton size="small" sx={{ mr: 1 }}>
                              {isExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                            </IconButton>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={() => handleVODCSelect(changeNumber)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{changeNumber}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {dc?.change_name || 'No name'} • {formatCurrency(dc?.boq_value || 0, dc?.currency || 'SGD', companyData.number_format)}
                              </Typography>
                            </Box>
                            {selectedItems.length > 0 && (
                              <Chip label={`${selectedItems.length} selected`} size="small" color="primary" sx={{ mr: 1 }} />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {Object.values(voSelectedBoqItems).some((items: any) => items.length > 0) && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {Object.values(voSelectedBoqItems).reduce((sum: number, items: any) => sum + items.length, 0)} BOQ item(s) selected from{' '}
                    {Object.keys(voSelectedBoqItems).filter(dc => voSelectedBoqItems[dc].length > 0).length} DC(s)
                  </Alert>
                )}
              </Box>
            )}

            {/* Budget Lines / BOQ Section (Edit Dialog) — shows ALL lines: BOQ + manual.
                Shown for VOs (create + edit) and for Contracts only in edit mode. */}
            {(formValues.contract_type === 'variation_order' ||
              (dialogMode === 'edit' && formValues.contract_type !== 'variation_order')) && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.68rem' }}>
                    Bill of Quantities (BOQ)
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addEditManualLine}>
                    Add Line
                  </Button>
                </Box>
                {editManualLines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {formValues.contract_type === 'variation_order'
                      ? 'No budget lines yet. Select a Design Change above to copy BOQ items, or click "Add Line" to add manually.'
                      : 'No budget lines yet. Click "Add Line" to add items.'}
                  </Typography>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    <DataGrid
                      rows={editManualLines}
                      columns={editManualLineColumns}
                      getRowId={(row) => row.id}
                      editMode="row"
                      rowModesModel={editManualRowModesModel}
                      onRowModesModelChange={setEditManualRowModesModel}
                      onRowDoubleClick={(params) => {
                        if (params.row) {
                          setEditManualRowModesModel(prev => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
                        }
                      }}
                      onRowEditStop={(params, event) => {
                        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                          event.defaultMuiPrevented = true;
                        }
                      }}
                      processRowUpdate={processEditManualLineRowUpdate}
                      onProcessRowUpdateError={(error) => console.error('Edit manual line error:', error)}
                      autoHeight
                      pageSizeOptions={[10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      rowHeight={48}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '& .MuiDataGrid-cell': { paddingX: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.875rem' },
                        '& .MuiDataGrid-row': {
                          '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 },
                          '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' }
                        },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            ref={saveButtonRef}
            variant="contained"
            onClick={() => handleSubmit(false)}
            disabled={saving}
          >
            {saving ? 'Saving?' : 'Save'}
          </Button>
          {dialogMode === 'edit' && selectedContract && (selectedContract.status === 'Draft' || selectedContract.status === 'Rejected') && (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleSubmit(true)}
              disabled={saving}
            >
              Send for Approval
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={(snackbar.severity || 'info') as AlertColor} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Documents Dialog */}
      <Dialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Contract Documents
            </Typography>
            {selectedContractForDocs && (
              <Typography variant="body2" color="text.secondary">
                {selectedContractForDocs.contract_number} - {selectedContractForDocs.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDocumentsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedContractForDocs && (
            <ContractDocuments
              contractId={selectedContractForDocs.contract_id}
              projectId={projectId}
              canWrite={canWrite}
              onDocumentCountChange={() => fetchContracts()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Import Design Changes Dialog */}
      <Dialog
        open={importDCDialogOpen}
        onClose={handleCloseImportDC}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Import Design Changes
          <IconButton onClick={handleCloseImportDC} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {importDCError && (
            <Alert severity="error" sx={{ mb: 2 }}>{importDCError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select design changes to import into this contract. Items with the same budget line item ID will have their quantities merged.
          </Typography>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {designChanges.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No design changes available for this project.
              </Typography>
            ) : (
              designChanges.map((dc: any) => (
                <Box
                  key={dc.change_number}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedDCs.includes(dc.change_number) ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleDCSelect(dc.change_number)}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Checkbox
                      checked={selectedDCs.includes(dc.change_number)}
                      onChange={() => handleDCSelect(dc.change_number)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {dc.change_number}
                      </Typography>
                      {dc.change_name && (
                        <Typography variant="body2" color="text.secondary">
                          {dc.change_name}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))
            )}
          </Box>
          {selectedDCs.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {selectedDCs.length} design change{selectedDCs.length > 1 ? 's' : ''} selected
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDC} disabled={importingDC}>
            Cancel
          </Button>
          <Button
            onClick={handleImportDCSubmit}
            variant="contained"
            disabled={importingDC || selectedDCs.length === 0}
            startIcon={importingDC ? <CircularProgress size={16} /> : null}
          >
            {importingDC ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Variation Order Dialog */}
      <Dialog
        open={voDialogOpen}
        onClose={handleCloseVODialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Create Variation Order
          <IconButton onClick={handleCloseVODialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {voDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>{voDialogError}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                required
                fullWidth
                label="VO Number"
                name="contract_number"
                value={voFormValues.contract_number}
                onChange={handleVOFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={voFormValues.title}
                onChange={handleVOFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Signed Date"
                name="signed_date"
                type="date"
                value={voFormValues.signed_date || ''}
                onChange={handleVOFormChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* VOs don't have original value - only BOQ value */}
            {/* <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                required
                fullWidth
                label="Original Value"
                name="original_value"
                type="number"
                value={voFormValues.original_value}
                onChange={handleVOFormChange}
                inputProps={{ step: '0.01' }}
              />
            </Grid> */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Currency"
                name="currency"
                value={voFormValues.currency}
                onChange={handleVOFormChange}
              >
                {['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'].map(code => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Rate Sheet"
                name="rate_sheet_id"
                value={voFormValues.rate_sheet_id || ''}
                onChange={handleVOFormChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {rateSheets.map(sheet => (
                  <MenuItem key={sheet.rate_sheet_id} value={sheet.rate_sheet_id}>
                    {sheet.sheet_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DC Selection Section */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Select Design Changes to Include in VO
              </Typography>
              <Autocomplete
                multiple
                options={designChanges}
                getOptionLabel={(opt: any) => `${opt.change_number}${opt.change_name ? ' — ' + opt.change_name : ''}`}
                value={designChanges.filter((dc: any) => voSelectedDCs.includes(dc.change_number))}
                onChange={(_, newValue: any[]) => {
                  const newChangeNumbers = newValue.map((dc: any) => dc.change_number);
                  const removed = voSelectedDCs.filter(cn => !newChangeNumbers.includes(cn));
                  if (removed.length > 0) {
                    setVoSelectedBoqItems(prev => {
                      const next = { ...prev };
                      removed.forEach(cn => { delete next[cn]; });
                      return next;
                    });
                    setVoManualLines(prev => prev.filter((l: any) => !removed.includes(l.source_change_number)));
                  }
                  setVoSelectedDCs(newChangeNumbers);
                }}
                isOptionEqualToValue={(opt: any, val: any) => opt.change_number === val.change_number}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Design Changes"
                    placeholder={voSelectedDCs.length === 0 ? 'Type to search by number or name...' : ''}
                    size="small"
                  />
                )}
                noOptionsText="No design changes available for this project"
                sx={{ mb: 2 }}
              />
              {voSelectedDCs.length > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  {designChanges
                    .filter((dc: any) => voSelectedDCs.includes(dc.change_number))
                    .map((dc: any) => {
                      const isExpanded = voExpandedDCs.includes(dc.change_number);
                      const boqItems = voDcBoqData[dc.change_number] || [];
                      const selectedItems = voSelectedBoqItems[dc.change_number] || [];
                      const allSelected = boqItems.length > 0 && selectedItems.length === boqItems.length;
                      const someSelected = selectedItems.length > 0 && selectedItems.length < boqItems.length;
                      return (
                        <Box key={dc.change_number} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: isExpanded ? 'action.hover' : 'transparent', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                            <IconButton size="small" onClick={() => handleVOToggleDCExpand(dc.change_number)} sx={{ mr: 1 }}>
                              {isExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                            </IconButton>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={() => handleVODCSelect(dc.change_number)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Box sx={{ flex: 1 }} onClick={() => handleVOToggleDCExpand(dc.change_number)}>
                              <Typography variant="body2" fontWeight={600}>{dc.change_number}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {dc.change_name || 'No name'} • {formatCurrency(dc.boq_value || 0, dc.currency || 'SGD', companyData.number_format)}
                              </Typography>
                            </Box>
                            {selectedItems.length > 0 && (
                              <Chip label={`${selectedItems.length} selected`} size="small" color="primary" sx={{ mr: 1 }} />
                            )}
                          </Box>
                          {isExpanded && (
                            <Box sx={{ bgcolor: 'background.default', p: 1, pl: 6 }}>
                              {voLoadingBoqData ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                  <CircularProgress size={20} sx={{ mr: 2 }} />
                                  <Typography variant="caption" color="text.secondary">Loading BOQ items...</Typography>
                                </Box>
                              ) : boqItems.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No BOQ items in this Design Change</Typography>
                              ) : (
                                boqItems.map((boqItem: any) => {
                                  const isItemSelected = selectedItems.includes(boqItem.dc_boq_line_id);
                                  return (
                                    <Box key={boqItem.dc_boq_line_id} sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                      <Checkbox size="small" checked={isItemSelected} onChange={() => handleVOBoqItemSelect(dc.change_number, boqItem.dc_boq_line_id)} />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" fontWeight={500}>{boqItem.item_description || 'No description'}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {formatNumber(boqItem.qty, companyData.number_format, 2)} {boqItem.unit_code} × {formatCurrency(boqItem.unit_price, boqItem.currency || 'SGD', companyData.number_format)} = {formatCurrency(boqItem.line_total || 0, boqItem.currency || 'SGD', companyData.number_format)}
                                          {boqItem.category_name && ` • ${boqItem.category_name}`}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              )}
              {Object.values(voSelectedBoqItems).some((items: any) => items.length > 0) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {Object.values(voSelectedBoqItems).reduce((sum: number, items: any) => sum + items.length, 0)} BOQ item(s) selected from{' '}
                  {Object.keys(voSelectedBoqItems).filter(dc => voSelectedBoqItems[dc].length > 0).length} DC(s)
                </Alert>
              )}
            </Grid>

            {/* Budget Lines Section — shows BOQ items (from DC) + manual lines */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Budget Line Items
                </Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addVoManualLine}>
                  Add Line
                </Button>
              </Box>
              {voManualLines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Select a Design Change above and click "Yes" to copy BOQ items here, or click "Add Line" to add manually.
                </Typography>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    rows={voManualLines}
                    columns={voManualLineColumns}
                    getRowId={(row) => row.id}
                    editMode="row"
                    rowModesModel={voManualRowModesModel}
                    onRowModesModelChange={setVoManualRowModesModel}
                    onRowDoubleClick={(params) => {
                      if (params.row) {
                        setVoManualRowModesModel(prev => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
                      }
                    }}
                    onRowEditStop={(params, event) => {
                      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                        event.defaultMuiPrevented = true;
                      }
                    }}
                    processRowUpdate={processVoManualLineRowUpdate}
                    onProcessRowUpdateError={(error) => console.error('VO manual line error:', error)}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    rowHeight={48}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '& .MuiDataGrid-cell': { paddingX: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.875rem' },
                      '& .MuiDataGrid-row': {
                        '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 },
                        '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' }
                      },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVODialog} disabled={creatingVO}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateVOSubmit}
            variant="contained"
            disabled={creatingVO}
            startIcon={creatingVO ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {creatingVO ? 'Creating...' : 'Create Variation Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy BOQ Confirmation Dialog */}
      <Dialog open={copyBoqConfirmOpen} onClose={handleCopyBoqDecline} maxWidth="xs" fullWidth>
        <DialogTitle>Copy BOQ Items?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Do you want to copy all BOQ items from <strong>{copyBoqTargetDC}</strong> into this VO?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can still modify, delete, or add line items after copying.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyBoqDecline} variant="outlined">No, Skip</Button>
          <Button onClick={handleCopyBoqConfirm} variant="contained">Yes, Copy</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {deleteType === 'contract' && `Are you sure you want to delete contract ${itemToDelete?.contract_number} and all its budget lines?`}
            {deleteType === 'vo' && `Are you sure you want to delete VO ${itemToDelete?.contract_number} and all its budget lines?`}
            {deleteType === 'budgetLine' && 'Are you sure you want to delete this budget line?'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Import Results
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {importResults && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Created:</strong> {importResults.created || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Errors:</strong> {importResults.total_errors || 0}
                </Typography>
              </Box>

              {importResults.errors && importResults.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Import Errors:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importResults.errors.map((err: any, idx: number) => (
                      <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                        Row {err.row}: {err.field} - {err.error}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              {(!importResults.errors || importResults.errors.length === 0) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  All contracts/VOs imported successfully!
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleImportDialogClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All Contracts
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> contracts and VOs for this project?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all contracts, VOs, their budget lines, and contract-design-change links from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined" disabled={deletingAll}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deletingAll}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* View Contract/VO Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedViewContract?.contract_type === 'variation_order' ? 'Variation Order' : 'Contract'} Details
            </Typography>
            <IconButton onClick={() => setViewDialogOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {selectedViewContract && (
            <Box className="doc-preview" sx={{ background: 'white', color: '#222', p: 3 }}>

              {/* Company Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2.5, borderBottom: '2px solid #e0e0e0' }}>
                {companyData.logo_url && (
                  <Box component="img" src={companyData.logo_url} alt="logo"
                    sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }} />
                )}
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{companyData.company_name || '—'}</Typography>
                  {companyData.address && <Typography variant="body2" color="text.secondary">{companyData.address}</Typography>}
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    {companyData.phone && <Typography variant="body2"><strong>Tel:</strong> {companyData.phone}</Typography>}
                    {companyData.email && <Typography variant="body2"><strong>Email:</strong> {companyData.email}</Typography>}
                  </Box>
                </Box>
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                    {selectedViewContract.contract_type === 'variation_order' ? 'VARIATION ORDER' : 'CONTRACT'}
                  </Typography>
                  <Chip
                    label={selectedViewContract.status || 'Draft'}
                    size="small"
                    sx={{
                      mt: 0.5, fontWeight: 600,
                      bgcolor: selectedViewContract.status === 'Approved' ? '#e8f5e9' : selectedViewContract.status === 'Rejected' ? '#ffebee' : selectedViewContract.status === 'Pending Approval' ? '#fff8e1' : '#e3f2fd',
                      color: selectedViewContract.status === 'Approved' ? '#2e7d32' : selectedViewContract.status === 'Rejected' ? '#c62828' : selectedViewContract.status === 'Pending Approval' ? '#e65100' : '#1565c0',
                      border: `1px solid ${selectedViewContract.status === 'Approved' ? '#a5d6a7' : selectedViewContract.status === 'Rejected' ? '#ef9a9a' : selectedViewContract.status === 'Pending Approval' ? '#ffcc80' : '#90caf9'}`
                    }}
                  />
                </Box>
              </Box>

              {/* Rejection Banner */}
              {selectedViewContract.rejection_comments && (
                <Box sx={{ p: 2, mb: 3, bgcolor: '#fff5f5', border: '1px solid #ffcdd2', borderLeft: '4px solid #f44336', borderRadius: 1, display: 'flex', gap: 1.5 }}>
                  <Typography sx={{ color: '#f44336', fontWeight: 'bold', fontSize: 18, lineHeight: 1.2 }}>⚠</Typography>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>Rejection Comments</Typography>
                    <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {selectedViewContract.rejection_comments}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Contract Details Grid */}
              {(() => {
                const cur = selectedViewContract.currency || 'SGD';
                const boqTotal = viewBOQLines.reduce((s, r) => s + (r.quantity || 0) * (r.unit_price || 0), 0);
                const spent = (selectedViewContract.total_value || 0) - (selectedViewContract.remaining_balance || 0);
                const budget = selectedViewContract.budget;
                const balance = budget != null ? budget - spent : null;
                const isOver = balance !== null && balance < 0;
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2.5, mb: 3 }}>
                    {[
                      { label: 'Contract Number', value: selectedViewContract.contract_number || '—', color: undefined },
                      { label: 'Name / Title', value: selectedViewContract.title || selectedViewContract.contract_name || '—', color: undefined },
                      { label: 'Currency', value: cur, color: undefined },
                      { label: 'Original Contract Value', value: formatCurrency(selectedViewContract.original_value, cur, companyData.number_format), color: undefined },
                      { label: 'Original BOQ Value', value: viewBOQLoading ? '...' : formatCurrency(boqTotal, cur, companyData.number_format), color: undefined },
                      { label: 'Approved Budget', value: budget != null ? formatCurrency(budget, cur, companyData.number_format) : 'N/A', color: undefined },
                      { label: 'Spent (MR/PO)', value: formatCurrency(spent, cur, companyData.number_format), color: undefined },
                      { label: 'Budget Balance', value: balance !== null ? formatCurrency(balance, cur, companyData.number_format) : 'N/A', color: undefined },
                      { label: 'Created At', value: selectedViewContract.created_at ? new Date(selectedViewContract.created_at).toLocaleDateString() : '—', color: undefined },
                    ].map(({ label, value, color }) => (
                      <Box key={label}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>{label}</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.3, color: color || 'inherit' }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* Associated Design Changes — VO only */}
              {selectedViewContract?.contract_type === 'variation_order' && viewLinkedDCs.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 1 }}>
                    Reference
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                    {viewLinkedDCs.map((dc: any) => (
                      <Chip
                        key={dc.change_number}
                        label={dc.change_name ? `${dc.change_number} — ${dc.change_name}` : dc.change_number}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              <Divider sx={{ mb: 2.5 }} />

              {/* BOQ Section */}
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
                Bill of Quantities (BOQ)
              </Typography>
              {viewBOQLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                  <DataGrid
                    rows={viewBOQLines}
                    columns={[
                      { field: 'line_no', headerName: 'Line No', width: 90 },
                      { field: 'description', headerName: 'Description', flex: 1, minWidth: 150 },
                      { field: 'quantity', headerName: 'Qty', width: 90, type: 'number' },
                      { field: 'uom', headerName: 'UOM', width: 90 },
                      { field: 'unit_price', headerName: 'Unit Price', width: 120, type: 'number',
                        valueFormatter: (value) => value != null ? formatCurrency(value, selectedViewContract?.currency || 'SGD', companyData.number_format) : '—' },
                      { field: 'line_total', headerName: 'Line Total', width: 130, type: 'number',
                        valueGetter: (value, row) => (row.quantity || 0) * (row.unit_price || 0),
                        valueFormatter: (value) => value != null ? formatCurrency(value, selectedViewContract?.currency || 'SGD', companyData.number_format) : '—' },
                      { field: 'pm_comment', headerName: 'PM Comment', flex: 1, minWidth: 150,
                        renderCell: (params) => (
                          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5, py: 0.5, fontSize: '0.85rem' }}>
                            {params.value || ''}
                          </Typography>
                        )
                      },
                    ]}
                    getRowId={(row) => row.line_id || row.id || Math.random()}
                    getRowHeight={() => 'auto'}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        printOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                      },
                    }}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-toolbarContainer': {
                        padding: '8px 12px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'grey.100',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        fontSize: '0.8rem',
                        fontWeight: 700
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700,
                        fontSize: '0.8rem'
                      },
                    }}
                  />
                </Box>
              )}
              {viewBOQLines.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Box sx={{ minWidth: 260 }}>
                    <Divider sx={{ mb: 0.8 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Total BOQ Cost</Typography>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">
                        {formatCurrency(
                          viewBOQLines.reduce((sum, r) => sum + (r.quantity || 0) * (r.unit_price || 0), 0),
                          selectedViewContract?.currency || 'SGD',
                          companyData.number_format
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined" size="small">Close</Button>
          {canWrite && selectedViewContract && selectedViewContract.status !== 'Approved' && (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => {
                const contractToEdit = selectedViewContract;
                setViewDialogOpen(false);
                handleOpenEdit(contractToEdit);
              }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const prev = document.title;
              document.title = `${selectedViewContract?.contract_type === 'variation_order' ? 'VO' : 'Contract'}-${selectedViewContract?.contract_number || 'draft'}`;
              window.print();
              document.title = prev;
            }}
          >
            Print / PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Source Dialog */}
      <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
        <DialogTitle>Import From Excel</DialogTitle>
        <DialogContent>Choose source type:</DialogContent>
        <DialogActions>
          <Button onClick={() => handleImportSource(false)}>Template</Button>
          <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
          <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog open={mappingOpen} onClose={() => { setMappingOpen(false); setFileHeaders([]); setMapFile(null); setMapping({}); }} maxWidth="md" fullWidth>
        <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 8 }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
          </div>
          {!!fileHeaders.length && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {CONTRACT_FIELDS.map(f => (
                <div key={f}>
                  <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                  <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [f]: e.target.value }))}>
                    <MenuItem value="">(none)</MenuItem>
                    {fileHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                  </TextField>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
          <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
        </DialogActions>
      </Dialog>

      {/* Importing Progress Dialog */}
      <Dialog open={excelImporting}>
        <DialogTitle>Importing...</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <LinearProgress variant="determinate" value={importProgress} />
          <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
        </DialogContent>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{importSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
        <DialogContent>
          {importSummaryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>Total contracts processed: <strong>{importSummaryData.total}</strong></Typography>
              <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
              <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
              <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
              <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
          <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QSContractOrVO;
